import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API, { fmt, pdfFilename } from '@/lib/api';
import { fetchPdfActions, openPdf as openPdfHelper, downloadPdf as downloadPdfHelper, sharePdf as sharePdfHelper, shareViaWhatsApp as whatsAppPdfHelper } from '@/lib/pdfActions';
import { useAuth } from '@/contexts/AuthContext';
import { useDocumentActionPreferences } from '@/hooks/useDocumentActionPreferences';
import { DocumentSummary } from '@/components/DocumentSummary';
import { InvoiceRowActions } from '@/components/InvoiceRowActions';
import { InvoicePaymentCell } from '@/components/InvoicePaymentCell';
import { InvoiceDetails, invoiceDate } from '@/components/InvoiceDetails';
import { DocumentActionPins } from '@/components/DocumentActionPins';
import { SignedDeliveryInvoice, isSignedUninvoicedDelivery, hasDeliverySignature } from '@/components/SignedDeliveryInvoice';
import { DeliveryStatusDetails } from '@/components/DeliveryStatusDetails';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';
import '@/components/invoice-list.css';
import '@/components/delivery-note-list.css';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { isIOS as isIOSPlatform } from '@/lib/platform';
import { vraagFotoKeuze, appendFotoParam, vraagPrijsKeuze } from '@/utils/pdfPromptFotos';
import { useConfirm } from '@/components/ConfirmDialog';
import { Plus, PencilSimple, Trash, ArrowRight, MagnifyingGlass, Receipt, FilePdf, Copy, EnvelopeSimple, CreditCard, DownloadSimple, CurrencyEur, PenNib, CheckCircle, XCircle, Truck, Scissors, WhatsappLogo, ArrowsIn, ArrowsOut, Tag } from '@phosphor-icons/react';
import IOSToggle from '@/components/IOSToggle';
import QuickSignModal from '@/components/QuickSignModal';
import CMRModal from '@/components/CMRModal';
import PartialCreditnotaModal from '@/components/PartialCreditnotaModal';
import MailPreviewModal from '@/components/MailPreviewModal';
import VerzendMeldingModal from '@/components/VerzendMeldingModal';
import DeelLeveringModal from '@/components/DeelLeveringModal';
import ChauffeurAdresModal from '@/components/ChauffeurAdresModal';
import AdresEtiketModal from '@/components/AdresEtiketModal';
import RemotePrintButton from '@/components/RemotePrintButton';
import BetalingsherinneringModal from '@/components/BetalingsherinneringModal';
import { toast } from 'sonner';

// ─── Status-pill configuratie (identiek aan PDF-styling) ───────
const STATUS_PILL = {
  'betaald': { bg: '#16A34A', fg: '#fff', icon: '✓', label: 'Betaald' },
  'overbetaald': { bg: '#7C3AED', fg: '#fff', icon: '✓', label: 'Overbetaald' },
  'gedeeltelijk betaald': { bg: '#F59E0B', fg: '#fff', icon: '◐', label: 'Deels betaald' },
  'vervallen': { bg: '#DC2626', fg: '#fff', icon: '!', label: 'Vervallen' },
  'openstaand': { bg: '#D97706', fg: '#fff', icon: '◐', label: 'Openstaand' },
  'concept': { bg: '#6B7280', fg: '#fff', icon: '✎', label: 'Concept' },
  'goedgekeurd': { bg: '#16A34A', fg: '#fff', icon: '✓', label: 'Goedgekeurd' },
  'geleverd': { bg: '#16A34A', fg: '#fff', icon: '✓', label: 'Geleverd' },
  'besteld': { bg: '#16A34A', fg: '#fff', icon: '✓', label: 'Besteld' },
  'afgewezen': { bg: '#DC2626', fg: '#fff', icon: '✕', label: 'Afgewezen' },
  'verzonden': { bg: '#002FA7', fg: '#fff', icon: '→', label: 'Verzonden' },
  'gedeeltelijk_geleverd': { bg: '#0EA5E9', fg: '#fff', icon: '◐', label: 'Deels geleverd' },
  'afgewerkt': { bg: '#16A34A', fg: '#fff', icon: '✓', label: 'Afgewerkt' },
  'geblokkeerd': { bg: '#B91C1C', fg: '#fff', icon: '⊘', label: 'Geblokkeerd' },
  'geannuleerd': { bg: '#FCA5A5', fg: '#7F1D1D', icon: '✕', label: 'Geannuleerd' },
  'productie in afwachting': { bg: '#7C3AED', fg: '#fff', icon: '⚙', label: 'In productie' },
  'nog te leveren': { bg: '#EA580C', fg: '#fff', icon: '◐', label: 'Nog te leveren' },
  'onderweg': { bg: '#0891B2', fg: '#fff', icon: '➜', label: 'Onderweg' },
};
const statusStyle = (s) => STATUS_PILL[(s || '').toLowerCase()] || { bg: '#6B7280', fg: '#fff', icon: '•', label: s || 'Onbekend' };

// Bereken fixed positie voor status-dropdowns zodat ze nooit onder de pagina/tabel verdwijnen
function statusMenuPos(rect, count) {
  const menuH = Math.min(count * 33 + 8, 320);
  const below = window.innerHeight - rect.bottom;
  const top = below >= menuH + 12 ? rect.bottom + 4 : Math.max(8, rect.top - menuH - 6);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 196));
  return { top, left };
}

function actionMenuPos(rect) {
  const width = Math.min(320, window.innerWidth - 16);
  const height = Math.min(520, window.innerHeight - 16);
  return {
    top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - height - 8)),
    left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
    width,
  };
}

function StatusPillSelect({ status, options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const sluit = () => setOpen(false);
    document.addEventListener('mousedown', h);
    window.addEventListener('scroll', sluit, true);
    window.addEventListener('resize', sluit);
    return () => { document.removeEventListener('mousedown', h); window.removeEventListener('scroll', sluit, true); window.removeEventListener('resize', sluit); };
  }, [open]);
  const cur = statusStyle(status);
  return (
    <div className="relative inline-block" ref={ref}>
      <button type="button"
        className="inline-flex items-center justify-center gap-1 px-2 py-1 text-[9.5px] font-bold uppercase tracking-wide hover:opacity-90 cursor-pointer transition-opacity"
        style={{ background: cur.bg, color: cur.fg, lineHeight: 1.2, whiteSpace: 'nowrap', width: 128 }}
        onClick={(e) => { e.stopPropagation(); if (!open) setPos(statusMenuPos(e.currentTarget.getBoundingClientRect(), options.length)); setOpen(o => !o); }}
        data-testid={testId}>
        <span>{cur.icon}</span>
        <span>{cur.label}</span>
        <span className="text-[8px] opacity-70 ml-0.5">▼</span>
      </button>
      {open && (
        <div className="bg-white border border-gray-200 shadow-xl min-w-[180px]" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 100, maxHeight: 320, overflowY: 'auto' }} data-testid={`${testId}-menu`}>
          {options.map(o => {
            const s = statusStyle(o);
            const active = (status || '').toLowerCase() === o.toLowerCase();
            return (
              <button key={o} type="button"
                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 ${active ? 'bg-gray-50' : ''}`}
                onClick={() => { onChange(o); setOpen(false); }}
                data-testid={`${testId}-opt-${o.replace(/\s+/g, '-')}`}>
                <span className="inline-block w-2.5 h-2.5 shrink-0" style={{ background: s.bg }} />
                <span className="font-medium text-gray-700 flex-1">{s.label}</span>
                {active && <span className="text-[#002FA7] font-bold">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Dual-segment factuur status pill met vervaldagen ───────────
// Links: label op lichte achtergrond. Rechts: dagen op volle kleur.
const FACT_STATUS_STYLE = {
  openstaand:           { label: 'Te Betalen',    light: '#E6F4EA', dark: '#166534', solid: '#397437' },
  'gedeeltelijk betaald': { label: 'Deels betaald', light: '#FEF3C7', dark: '#92400E', solid: '#D97706' },
  vervallen:            { label: 'Vervallen',     light: '#FEE2E2', dark: '#991B1B', solid: '#DC2626' },
  concept:              { label: 'Concept',       light: '#F3F4F6', dark: '#374151', solid: '#6B7280' },
  betaald:              { label: 'Betaald',       light: '#397437', dark: '#FFFFFF', solid: '#397437', full: true },
  overbetaald:          { label: 'Overbetaald',   light: '#7C3AED', dark: '#FFFFFF', solid: '#7C3AED', full: true },
  geblokkeerd:          { label: 'Geblokkeerd',   light: '#B91C1C', dark: '#FFFFFF', solid: '#B91C1C', full: true },
  geannuleerd:          { label: 'Geannuleerd',   light: '#FCA5A5', dark: '#7F1D1D', solid: '#FCA5A5', full: true },
};

function FactuurStatusPill({ status, verval, betaald_bedrag, totaal, options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const sluit = () => setOpen(false);
    document.addEventListener('mousedown', h);
    window.addEventListener('scroll', sluit, true);
    window.addEventListener('resize', sluit);
    return () => { document.removeEventListener('mousedown', h); window.removeEventListener('scroll', sluit, true); window.removeEventListener('resize', sluit); };
  }, [open]);

  const key = (status || '').toLowerCase();
  const st = FACT_STATUS_STYLE[key] || FACT_STATUS_STYLE.concept;

  // Dagen-berekening voor niet-betaalde facturen
  let dagenText = null, dagenBg = st.solid;
  let isOverdue = false;
  if (!st.full && verval && key !== 'concept') {
    const nu = new Date(); nu.setHours(0,0,0,0);
    const vd = new Date(verval); vd.setHours(0,0,0,0);
    const diff = Math.floor((vd - nu) / 86400000);
    if (diff < 0) { dagenText = `-${Math.abs(diff)}d`; dagenBg = '#DC2626'; isOverdue = true; }
    else if (diff === 0) { dagenText = 'Vandaag'; dagenBg = '#D97706'; }
    else if (Number.isFinite(diff)) { dagenText = `${diff}d`; dagenBg = st.solid; }
  }
  // Auto-promoteer "openstaand" label naar "Vervallen" wanneer vervaldatum gepasseerd
  // (deels betaald → behoud label, alleen rode kleur via solid)
  const effectiveSt = isOverdue && key === 'openstaand' ? FACT_STATUS_STYLE.vervallen : st;
  const vervalDate = verval && Number.isFinite(new Date(verval).getTime()) ? new Date(verval).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const tooltip = vervalDate ? `Vervaldatum: ${vervalDate}${isOverdue ? ' — verstreken' : ''}` : '';

  return (
    <div className="relative inline-block align-middle" ref={ref} onKeyDown={e => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); ref.current?.querySelector('button')?.focus(); }
    }}>
      <button
        type="button"
        className="inline-flex items-stretch text-[9.5px] font-bold uppercase hover:opacity-90 cursor-pointer transition-opacity"
        onClick={(e) => { e.stopPropagation(); if (!open) setPos(statusMenuPos(e.currentTarget.getBoundingClientRect(), options.length)); setOpen(o => !o); }}
        data-testid={testId}
        title={tooltip}
        aria-label={`Status ${effectiveSt.label} wijzigen`}
        aria-expanded={open}
        aria-haspopup="menu"
        style={{ lineHeight: 1, width: 180, height: 18, borderRadius: 3, overflow: 'hidden', fontSize: 10, textTransform: 'none' }}
      >
        <span
          className="px-1.5 inline-flex items-center justify-between gap-1"
          style={{ background: effectiveSt.light, color: effectiveSt.dark, whiteSpace: 'nowrap', flex: dagenText ? '1 1 auto' : '1 1 100%' }}
          data-testid={`${testId}-label`}
        >
          {effectiveSt.label}
          <span aria-hidden="true" className="text-[8px]">▾</span>
        </span>
        {dagenText && (
          <span
            className="px-1 inline-flex items-center justify-center"
            style={{ background: dagenBg, color: '#fff', whiteSpace: 'nowrap', minWidth: 36, flex: '0 0 auto' }}
            data-testid={`${testId}-days`}
          >
            {dagenText}
          </span>
        )}
      </button>
      {open && (
        <div role="menu" className="bg-white border border-gray-200 shadow-xl min-w-[180px]" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 100, maxHeight: 320, overflowY: 'auto' }} data-testid={`${testId}-menu`}>
          {options.map(o => {
            const os = FACT_STATUS_STYLE[o.toLowerCase()] || FACT_STATUS_STYLE.concept;
            const active = key === o.toLowerCase();
            return (
              <button key={o} type="button"
                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 ${active ? 'bg-gray-50' : ''}`}
                onClick={() => { onChange(o); setOpen(false); }}
                data-testid={`${testId}-opt-${o.replace(/\s+/g, '-')}`}
              >
                <span className="inline-block w-2.5 h-2.5 shrink-0" style={{ background: os.solid }} />
                <span className="font-medium text-gray-700 flex-1">{os.label}</span>
                {active && <span className="text-[#002FA7] font-bold">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}



const TYPE_CFG = {
  factuur: {
    label: 'Facturen', single: 'Factuur', pfx: 'FAC',
    statuses: ['concept', 'openstaand', 'gedeeltelijk betaald', 'betaald', 'overbetaald', 'vervallen', 'geannuleerd', 'geblokkeerd'],
    conversions: [],
    showBetaalwijze: false,
  },
  offerte: {
    label: 'Offertes', single: 'Offerte', pfx: 'OFF',
    statuses: ['concept', 'verzonden', 'goedgekeurd', 'afgewezen', 'geannuleerd', 'geblokkeerd'],
    conversions: ['bestelbon', 'leverbon', 'proforma', 'factuur'],
  },
  bestelbon: {
    label: 'Bestelbonnen', single: 'Bestelbon', pfx: 'BB',
    statuses: ['concept', 'verzonden', 'productie in afwachting', 'besteld', 'gedeeltelijk_geleverd', 'afgewerkt', 'geannuleerd', 'geblokkeerd'],
    conversions: ['leverbon', 'factuur'],
  },
  leverbon: {
    label: 'Leveringsbonnen', single: 'Leveringsbon', pfx: 'LB',
    statuses: ['concept', 'verzonden', 'productie in afwachting', 'nog te leveren', 'onderweg', 'geleverd', 'geannuleerd', 'geblokkeerd'],
    conversions: ['factuur'],
    actionStatus: 'geleverd', actionLabel: 'Te Factureren', actionTarget: 'factuur',
  },
  proforma: {
    label: 'Proforma facturen', single: 'Proforma', pfx: 'PRO',
    statuses: ['concept', 'verzonden', 'geannuleerd', 'geblokkeerd'],
    conversions: ['factuur', 'leverbon', 'bestelbon'],
  },
  creditnota: {
    label: 'Creditnota\'s', single: 'Creditnota', pfx: 'CN',
    statuses: ['concept', 'verzonden', 'verwerkt', 'geannuleerd', 'geblokkeerd'],
    conversions: [],
  },
};

const TYPE_LABELS = { offerte: 'Offerte', bestelbon: 'Bestelbon', leverbon: 'Leverbon', proforma: 'Proforma', factuur: 'Factuur' };
const BADGE_MAP = {
  'betaald': 'badge-green', 'goedgekeurd': 'badge-green', 'geleverd': 'badge-green', 'besteld': 'badge-green',
  'overbetaald': 'badge-purple',
  'gedeeltelijk betaald': 'badge-yellow',
  'openstaand': 'badge-yellow', 'verzonden': 'badge-blue', 'concept': 'badge-gray',
  'productie in afwachting': 'badge-purple',
  'nog te leveren': 'badge-orange',
  'onderweg': 'badge-blue',
  'vervallen': 'badge-red', 'afgewezen': 'badge-red',
  'geannuleerd': 'badge-gray',
  'geblokkeerd': 'badge-red',
};
const BETAALWIJZEN = ['Overschrijving', 'Contant', 'Bancontact', 'Domiciliering', 'Cheque', 'Payconiq', 'Andere'];

// Machtigingen per docType — moet exact matchen met Layout.js sidebar-permKeys
const DOCTYPE_PERMKEY = {
  factuur: 'facturen_toegang',
  proforma: 'facturen_toegang',
  leverbon: 'leverbonnen_toegang',
  offerte: 'offertes_toegang',
  creditnota: 'creditnotas_toegang',
  bestelbon: 'bestellingen_aanmaken',
  aankoop: 'aankopen_actief',
};

export default function DocListPage({ docType, bronFilter = null, titleOverride = null, subtitleOverride = null }) {
  const { user } = useAuth();
  const { isVisible } = useCompanyFunctions();
  const mayCreateInvoice = user?.role !== 'alleen-lezen' && (user?.role === 'admin' || !!user?.machtigingen?.facturen_toegang) && isVisible('/facturen');
  const conversionLocks = useRef(new Set());
  const [converting, setConverting] = useState({});
  const activeList = useRef('');
  const listIdentity = `${user?._id || ''}:${user?.verkoper_id || ''}:${docType}`;
  activeList.current = listIdentity;
  useEffect(() => { activeList.current = listIdentity; return () => { activeList.current = ''; }; }, [listIdentity]);
  const confirm = useConfirm();
  const [docs, setDocs] = useState([]);
  const [klanten, setKlanten] = useState([]);
  const [artikels, setArtikels] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [inst, setInst] = useState({});
  const [statusF, setStatusF] = useState('');
  const [verkoperF, setVerkoperF] = useState('');
  const [verkopers, setVerkopers] = useState([]);
  const [q, setQ] = useState('');
  // ─── Datum-filter (van / tot) ──────────────────────────────
  const [datumVan, setDatumVan] = useState('');
  const [datumTot, setDatumTot] = useState('');
  const [verzendModal, setVerzendModal] = useState(null); // { did, chauffeur_id } of { ids:[...], chauffeur_id } voor bulk
  const [meldVerzendModal, setMeldVerzendModal] = useState(null); // { leverbonnen: [{nummer,klant_id,klant,gsm,email}], bedrijfsnaam } na succesvolle verzending
  const [signModal, setSignModal] = useState(null); // { did, klantNaam, klantGsm, klantEmail }
  const [cmrModal, setCmrModal] = useState(null); // { did, klantNaam }
  const [partialCnModal, setPartialCnModal] = useState(null); // { did }
  const [selectedIds, setSelectedIds] = useState([]);
  const [versturenModal, setVersturenModal] = useState(null); // { doc, opties, historiek, busy }
  const [previewDoc, setPreviewDoc] = useState(null); // doc voor MailPreviewModal (offerte/proforma/etc.)
  const [deelLeverModal, setDeelLeverModal] = useState(null); // { bestelbonId } voor DeelLeveringModal
  const [chauffeurAdresModal, setChauffeurAdresModal] = useState(null); // { leverbonId } of { leverbonIds: [] } voor ChauffeurAdresModal
  const [etiketModal, setEtiketModal] = useState(null); // { docIds: [] } voor AdresEtiketModal
  const [bhModal, setBhModal] = useState(null); // { klantId, ids: [] } voor bulk Betalingsherinnering vanuit Facturenlijst
  const [voortgangMap, setVoortgangMap] = useState({}); // { BB-ID: {totaal_te_leveren, volledig_geleverd, aantal_leverbonnen, lijnen_open} }
  // 💰 Winst per document — null = geen toegang (machtiging), {} = geladen map
  const [winstMap, setWinstMap] = useState(null);
  const [winstKolom, setWinstKolom] = useState(() => {
    try { return localStorage.getItem('doclist_winst_kolom') !== '0'; } catch { return true; }
  });
  // Paginatie — persistente voorkeur per docType (bv. veel facturen/dag → grote pagina)
  const [pageSize, setPageSize] = useState(() => {
    try { const v = parseInt(localStorage.getItem(`doclist_pageSize_${docType}`) || '25', 10); return [10, 25, 50, 100, 250, 500].includes(v) ? v : 25; }
    catch { return 25; }
  });
  const [page, setPage] = useState(1);
  const [density, setDensity] = useState(() => localStorage.getItem('bf_table_density') || 'compact');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Reset naar pagina 1 bij wijziging filter / zoek / pageSize
  useEffect(() => { setPage(1); }, [statusF, verkoperF, q, datumVan, datumTot, pageSize, docType]);
  useEffect(() => {
    try { localStorage.setItem(`doclist_pageSize_${docType}`, String(pageSize)); } catch { /* quota */ }
  }, [pageSize, docType]);

  // Verkoper-filter via ?verkoper=xxx (bv. vanuit dashboard-widget)
  useEffect(() => {
    if (docType === 'bestelbon') {
      const vid = searchParams.get('verkoper');
      if (vid) setVerkoperF(vid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  const cfg = TYPE_CFG[docType] || TYPE_CFG.factuur;

  const load = (fresh = false) => {
    const scope = activeList.current;
    API.get('/documenten', { params: { type: docType }, noCache: fresh }).then(r => { if (activeList.current === scope) setDocs(r.data); }).catch(() => {});
    API.get('/klanten').then(r => setKlanten(r.data)).catch(() => {});
    API.get('/artikels').then(r => setArtikels(r.data)).catch(() => {});
    API.get('/instellingen').then(r => setInst(r.data)).catch(() => {});
    if (docType === 'leverbon') {
      API.get('/chauffeurs').then(r => setChauffeurs(r.data || [])).catch(() => {});
    }
    if (docType === 'bestelbon') {
      API.get('/verkopers').then(r => setVerkopers(r.data || [])).catch(() => {});
    }
    API.get(`/documenten-winst?type=${docType}`)
      .then(r => { if (activeList.current === scope) setWinstMap(r.data?.winsten || {}); })
      .catch(() => { if (activeList.current === scope) setWinstMap(null); });
  };
  useEffect(() => { load(); }, [docType]);

  // Bug-fix: wanneer de gebruiker van type wisselt (bv. Leverbon → Bestelbon),
  // leegmaken van selectie zodat acties zich niet per ongeluk op oude IDs richten.
  useEffect(() => {
    setSelectedIds([]);
  }, [docType]);

  // d (optioneel) = document: offerte voor niet-klant toont de prospect-firma
  const kNaam = (kid, d) => {
    const k = klanten.find(x => x.id === kid);
    if (k) return k.bedrijf || `${k.voorn} ${k.achtern}`;
    if (d?.prospect?.firma) return `${d.prospect.firma} (prospect)`;
    return d?.klant_naam || kid || '-';
  };
  const kFull = (kid, d) => klanten.find(x => x.id === kid) || (d?.prospect?.firma ? { bedrijf: d.prospect.firma, straat: d.prospect.straat, nr: d.prospect.nr, postcode: d.prospect.postcode, stad: d.prospect.stad, land: d.prospect.land, email: d.prospect.email, tel: d.prospect.tel, taal: d.prospect.taal, is_prospect: true } : {});
  const fmtAdres = (k) => {
    if (k.straat) return [k.straat + (k.nr ? ' ' + k.nr : ''), [k.postcode, k.stad].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return k.adres || '';
  };
  const fmtLevAdres = (k) => {
    if (k.lev_straat) return [k.lev_straat + (k.lev_nr ? ' ' + k.lev_nr : ''), [k.lev_postcode, k.lev_stad].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    return '';
  };

  const [pdfLayout, setPdfLayout] = useState('modern');

  const actiesPreferences = useDocumentActionPreferences(user, docType);
  const actiesVisible = actiesPreferences.visible;
  const anyActiesOn = actiesPreferences.enabled;
  const [actiesMenuOpen, setActiesMenuOpen] = useState(false);
  useEffect(() => { setActiesMenuOpen(false); }, [docType]);
  // Excel-export modal: van/tot/status filters
  const [showExcelModal, setShowExcelModal] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const firstOfYear = today.slice(0, 4) + '-01-01';
  const [excelForm, setExcelForm] = useState({
    van: firstOfYear, tot: today, status: '', klant_id: '', include_lijnen: false,
    via_email: false, to_email: '', cc_email: '', email_onderwerp: '', email_bericht: '',
    klant_zoek: '', email_taal: 'Nederlands', email_toon: 'herinnering', betaaltermijn_dagen: '',
    via_mailbox: '',  // '' = auto, 'skip' = legacy systeem, '<account_id>' = specifieke mailbox
  });
  const [excelBusy, setExcelBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [excelKlantenLijst, setExcelKlantenLijst] = useState([]);
  const [showKlantDropdown, setShowKlantDropdown] = useState(false);
  const [excelMailboxen, setExcelMailboxen] = useState([]);
  // Klanten + mailboxen laden voor de excel-modal (eenmaal — bij eerste open)
  const loadExcelKlanten = async () => {
    if (excelKlantenLijst.length) return;
    try {
      const r = await API.get('/klanten');
      setExcelKlantenLijst(r.data || []);
    } catch {}
  };
  const loadExcelMailboxen = async () => {
    try {
      const r = await API.get('/mail/accounts');
      setExcelMailboxen(r.data || []);
    } catch {
      // 402 (premium niet actief) of fout → lege lijst, dan default = legacy
      setExcelMailboxen([]);
    }
  };
  const openExcelModal = () => { setShowExcelModal(true); loadExcelKlanten(); loadExcelMailboxen(); };
  // Filter klanten op basis van zoekterm
  const filteredKlanten = useMemo(() => {
    const q = (excelForm.klant_zoek || '').trim().toLowerCase();
    const list = excelKlantenLijst.slice().sort((a, b) => (a.bedrijf || a.achtern || '').localeCompare(b.bedrijf || b.achtern || ''));
    if (!q) return list.slice(0, 50);
    return list.filter(k => {
      const naam = (k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`).toLowerCase();
      return naam.includes(q) || (k.email || '').toLowerCase().includes(q) || (k.btwnr || '').toLowerCase().includes(q);
    }).slice(0, 50);
  }, [excelForm.klant_zoek, excelKlantenLijst]);
  const selectedKlant = excelKlantenLijst.find(k => k.id === excelForm.klant_id);
  const selectKlant = (k) => {
    if (!k) {
      setExcelForm(p => ({ ...p, klant_id: '', klant_zoek: '' }));
    } else {
      const naam = k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim();
      setExcelForm(p => ({ ...p, klant_id: k.id, klant_zoek: naam }));
    }
    setShowKlantDropdown(false);
  };
  const doAiOpmaak = async () => {
    setAiBusy(true);
    try {
      const r = await API.post('/documenten/export-xlsx-ai-opmaak', {
        van: excelForm.van, tot: excelForm.tot, type: docType, status: excelForm.status,
        klant_id: excelForm.klant_id, taal: excelForm.email_taal,
        toon: excelForm.email_toon || 'herinnering',
        betaaltermijn_dagen: excelForm.betaaltermijn_dagen ? parseInt(excelForm.betaaltermijn_dagen) : undefined,
      });
      setExcelForm(p => ({ ...p, email_onderwerp: r.data.onderwerp || '', email_bericht: r.data.bericht || '' }));
    } catch (e) {
      alert('AI-opmaak mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setAiBusy(false);
  };
  const doOpenInMailApp = async () => {
    // Download eerst de Excel, dan open mailto: zodat user manueel kan attachen
    setExcelBusy(true);
    try {
      const baseParams = { type: docType, include_lijnen: excelForm.include_lijnen };
      if (excelForm.van) baseParams.van = excelForm.van;
      if (excelForm.tot) baseParams.tot = excelForm.tot;
      if (excelForm.status) baseParams.status = excelForm.status;
      if (excelForm.klant_id) baseParams.klant_id = excelForm.klant_id;
      // 1) Download Excel-bestand
      const r = await API.get('/documenten/export-xlsx', { params: baseParams, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fname = `${docType}-export-${excelForm.van || 'alle'}_tot_${excelForm.tot || 'nu'}.xlsx`;
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      // 2) Bouw mailto: link
      const klantNaam = selectedKlant ? (selectedKlant.bedrijf || `${selectedKlant.voorn || ''} ${selectedKlant.achtern || ''}`.trim()) : '';
      const periodeLabel = `${excelForm.van || 'begin'} t.e.m. ${excelForm.tot || 'nu'}`;
      const onderwerp = excelForm.email_onderwerp || `Documenten-export ${docType} — ${periodeLabel}${klantNaam ? ' — ' + klantNaam : ''}`;
      const ccLijst = (excelForm.cc_email || '').split(/[,;]/).map(e => e.trim()).filter(e => e.includes('@'));
      const lichaam = excelForm.email_bericht || '';
      const params = new URLSearchParams();
      params.set('subject', onderwerp);
      params.set('body', lichaam);
      if (ccLijst.length) params.set('cc', ccLijst.join(','));
      const mailtoUrl = `mailto:${encodeURIComponent(excelForm.to_email || '')}?${params.toString().replace(/\+/g, '%20')}`;
      window.location.href = mailtoUrl;
      // Korte toast/notificatie buiten de e-mail (vervuilt de body niet)
      setTimeout(() => {
        try {
          alert(`✓ Excel-bestand "${fname}" is gedownload.\n\nVoeg het manueel als bijlage toe in je mail-app (sleep het bestand in het e-mailvenster).`);
        } catch (_) {}
      }, 800);
      // Sluit niet automatisch — user kan eventueel nog "Verstuur per e-mail" gebruiken
    } catch (e) {
      alert('Kan mail-app niet openen: ' + (e.response?.data?.detail || e.message));
    }
    setExcelBusy(false);
  };
  const doExcelExport = async () => {
    setExcelBusy(true);
    try {
      const baseParams = { type: docType, include_lijnen: excelForm.include_lijnen };
      if (excelForm.van) baseParams.van = excelForm.van;
      if (excelForm.tot) baseParams.tot = excelForm.tot;
      if (excelForm.status) baseParams.status = excelForm.status;
      if (excelForm.klant_id) baseParams.klant_id = excelForm.klant_id;
      if (excelForm.via_email) {
        // Email-export
        if (!excelForm.to_email || !excelForm.to_email.includes('@')) {
          alert('Vul een geldig e-mailadres in om naar te sturen');
          setExcelBusy(false);
          return;
        }
        const ccLijst = (excelForm.cc_email || '').split(/[,;]/).map(e => e.trim()).filter(e => e.includes('@'));
        const klantNaam = selectedKlant ? (selectedKlant.bedrijf || `${selectedKlant.voorn || ''} ${selectedKlant.achtern || ''}`.trim()) : '';
        const periodeLabel = `${excelForm.van || 'begin'} t.e.m. ${excelForm.tot || 'nu'}`;
        const onderwerp = excelForm.email_onderwerp || `Documenten-export ${docType} — ${periodeLabel}${klantNaam ? ' — ' + klantNaam : ''}`;
        const r = await API.post('/documenten/export-xlsx-email', {
          ...baseParams,
          to: excelForm.to_email,
          cc: ccLijst,
          onderwerp,
          bericht: excelForm.email_bericht || '',
          via_mailbox: excelForm.via_mailbox || '',
        });
        const viaMH = r.data?.via_mail_hub;
        const provider = r.data?.provider || '';
        alert(`✓ Excel-export verzonden naar ${excelForm.to_email}` +
              (ccLijst.length ? ` (cc: ${ccLijst.join(', ')})` : '') +
              (viaMH ? `\n\n📬 Via je gekoppelde mailbox (${provider.replace('mail_hub:', '')}) — verschijnt in je Outlook/Gmail "Verzonden"-folder.` : ''));
        setShowExcelModal(false);
      } else {
        // Download
        const r = await API.get('/documenten/export-xlsx', { params: baseParams, responseType: 'blob' });
        const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${docType}-export-${excelForm.van || 'alle'}_tot_${excelForm.tot || 'nu'}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        setShowExcelModal(false);
      }
    } catch (e) {
      alert('Export mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setExcelBusy(false);
  };
  // Auto-fill to_email als klant geselecteerd
  useEffect(() => {
    if (excelForm.klant_id && excelForm.via_email && !excelForm.to_email) {
      const k = excelKlantenLijst.find(x => x.id === excelForm.klant_id);
      if (k?.email) setExcelForm(p => ({ ...p, to_email: k.email }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excelForm.klant_id, excelForm.via_email]);
  // Quick-presets voor datum
  const setRangeMonth = (offsetMonths = 0) => {
    const d = new Date();
    d.setMonth(d.getMonth() + offsetMonths);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, d.getMonth() + 1, 0).getDate();
    setExcelForm(p => ({ ...p, van: `${y}-${m}-01`, tot: `${y}-${m}-${String(lastDay).padStart(2, '0')}` }));
  };
  const setRangeYTD = () => setExcelForm(p => ({ ...p, van: new Date().getFullYear() + '-01-01', tot: today }));
  const setRangeYear = (year) => setExcelForm(p => ({ ...p, van: `${year}-01-01`, tot: `${year}-12-31` }));
  const [actiesMenuPos, setActiesMenuPos] = useState({ top: 8, left: 8, width: 320 });
  const actiesMenuRef = useRef(null);
  const actiesTriggerRef = useRef(null);
  useEffect(() => {
    if (!actiesMenuOpen) return;
    const h = (e) => {
      if (actiesMenuRef.current && !actiesMenuRef.current.contains(e.target)
          && actiesTriggerRef.current && !actiesTriggerRef.current.contains(e.target)) {
        setActiesMenuOpen(false);
      }
    };
    const reposition = () => {
      const rect = actiesTriggerRef.current?.getBoundingClientRect();
      if(rect) setActiesMenuPos(actionMenuPos(rect));
    };
    document.addEventListener('mousedown', h);
    window.addEventListener('resize', reposition);
    return () => { document.removeEventListener('mousedown', h); window.removeEventListener('resize', reposition); };
  }, [actiesMenuOpen]);
  const openActiesMenu = () => {
    const r = actiesTriggerRef.current?.getBoundingClientRect();
    if (r) setActiesMenuPos(actionMenuPos(r));
    setActiesMenuOpen(o => !o);
  };
  const toggleAllActies = actiesPreferences.toggleEnabled;
  const toggleActie = actiesPreferences.toggleAction;

  const showActies = anyActiesOn;

  const typeDocs = docs.filter(d => d.type === docType && (!bronFilter || (d.bron || '') === bronFilter));
  // Statuses die "te ontvangen" zijn = niet volledig betaald en geen concept/geannuleerd
  const TE_ONTVANGEN_STATUSES = ['openstaand', 'gedeeltelijk betaald', 'vervallen', 'verzonden'];
  const filtered = typeDocs.filter(d =>
    (!statusF
      || (statusF === '__te_factureren__'
            ? (cfg.actionStatus && d.status === cfg.actionStatus && !d.gefactureerd)
            : statusF === '__te_ontvangen__'
              ? (TE_ONTVANGEN_STATUSES.includes(d.status) && (Number(d.totaal || 0) - Math.abs(Number(d.creditnota_totaal || 0)) - Number(d.betaald_bedrag || 0)) > 0.02)
              : statusF === '__ontvangen__'
                ? (d.status === 'betaald' || (Number(d.betaald_bedrag || 0) > 0.02))
                : statusF === '__omzet__'
                  ? d.status !== 'concept'
                  : d.status === statusF)) &&
    (!verkoperF || d.verkoper_id === verkoperF) &&
    (!datumVan || (d.datum || '') >= datumVan) &&
    (!datumTot || (d.datum || '') <= datumTot) &&
    (!q || kNaam(d.klant_id, d).toLowerCase().includes(q.toLowerCase()) || d.id.toLowerCase().includes(q.toLowerCase()) || (d.verkoper_naam || '').toLowerCase().includes(q.toLowerCase()))
  ).sort((a, b) => (b._created || '').localeCompare(a._created || ''));

  // ─── Bulk-voortgang voor bestelbonnen (toont "Resterend X" badge) ──
  // Alleen voor bestelbonnen, één keer per zichtbare lijst (filtered.length veranderd → re-fetch)
  const bestelbonIds = useMemo(() => {
    if (docType !== 'bestelbon') return [];
    return typeDocs.map(d => d.id);
  }, [docType, typeDocs]);
  const bestelbonIdsKey = bestelbonIds.join(',');
  useEffect(() => {
    if (docType !== 'bestelbon' || bestelbonIds.length === 0) { setVoortgangMap({}); return; }
    let cancelled = false;
    API.post('/bestelbon/voortgang-bulk', { ids: bestelbonIds })
      .then(r => { if (!cancelled) setVoortgangMap(r.data || {}); })
      .catch(() => { if (!cancelled) setVoortgangMap({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, bestelbonIdsKey]);

  // Pagination berekeningen
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(Math.max(1, page), totalPages);
  const pagedDocs = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const del = async (id) => {
    if (!window.confirm('Document verwijderen?')) return;
    await API.delete(`/documenten/${id}`);
    load();
  };

  const omzetten = async (did, naar, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const lockKey = `${did}:${naar}`;
    const scope = activeList.current;
    if (conversionLocks.current.has(lockKey)) return;
    if (!window.confirm(`Omzetten naar ${TYPE_LABELS[naar]}?`)) return;
    conversionLocks.current.add(lockKey);
    setConverting(current => ({ ...current, [lockKey]: true }));
    try {
      const { data } = await API.post(`/documenten/${did}/omzetten`, { naar_type: naar });
      if (activeList.current !== scope) return;
      // Leverbon → factuur: factuur wordt aangemaakt + opgeslagen, we blijven op de leverbonnen-lijst
      // (user verwerkt meerdere leverbonnen na elkaar). Toast met "Openen" om de factuur alsnog te bewerken.
      if (data?.id && docType === 'leverbon' && naar === 'factuur') {
        setDocs(current => current.map(doc => doc.id === did ? { ...doc, gefactureerd: true, factuur_id: data.id, status: 'geleverd' } : doc));
        toast.success(`Factuur ${data.id} aangemaakt op basis van ${did}`, {
          duration: 8000,
          action: { label: 'Openen', onClick: () => navigate(`/documenten/${data.id}/bewerk`) },
        });
        load(true);
        return;
      }
      // Direct navigeren naar het nieuw aangemaakte document zodat user duidelijke feedback ziet
      if (data?.id) {
        navigate(`/documenten/${data.id}/bewerk`);
      } else {
        load();
      }
    } catch (err) {
      if (activeList.current !== scope) return;
      const msg = err?.response?.data?.detail || 'Omzetten mislukt';
      // Bij 409 (al omgezet): bied keuze om bestaand document te openen
      if (err?.response?.status === 409) {
        if (docType === 'leverbon' && naar === 'factuur') load(true);
        const idMatch = String(msg).match(/([A-Z]+-\d{4}-\d+)/);
        if (idMatch && window.confirm(`${msg}\n\n[OK] = Open bestaand document\n[Annuleer] = Blijf hier`)) {
          navigate(`/documenten/${idMatch[1]}/bewerk`);
          return;
        }
      }
      alert(msg);
    } finally {
      conversionLocks.current.delete(lockKey);
      setConverting(current => { const next = { ...current }; delete next[lockKey]; return next; });
    }
  };

  const bevestigShop = async (did) => {
    if (!window.confirm(`Bestelling ${did} bevestigen? De klant ontvangt automatisch een bevestigings-e-mail.`)) return;
    try {
      await API.post(`/shop-bestellingen/${did}/bevestig`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bevestigen mislukt');
    }
  };

  const weigerShop = async (did) => {
    const reden = window.prompt(`Bestelling ${did} weigeren?\n\nReden (optioneel — wordt naar klant gemaild):`);
    if (reden === null) return;
    try {
      await API.post(`/shop-bestellingen/${did}/weiger`, { reden });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Weigeren mislukt');
    }
  };

  const changeStatus = async (did, newStatus) => {
    // Speciale behandeling voor leverbon: registreer datum/uur + chauffeur
    if (docType === 'leverbon' && newStatus === 'verzonden') {
      setVerzendModal({ did, chauffeur_id: '' });
      return;
    }
    if (docType === 'leverbon' && newStatus === 'geleverd') {
      await API.post(`/leverbon/${did}/geleverd`);
      load();
      return;
    }
    await API.put(`/documenten/${did}`, { status: newStatus });
    setDocs(prev => prev.map(d => d.id === did ? { ...d, status: newStatus } : d));
  };

  const bevestigVerzend = async () => {
    if (!verzendModal) return;
    const body = verzendModal.chauffeur_id ? { chauffeur_id: verzendModal.chauffeur_id } : {};
    const isBulk = Array.isArray(verzendModal.ids);
    const verzondenIds = isBulk ? verzendModal.ids : [verzendModal.did];
    if (isBulk) {
      await Promise.all(verzendModal.ids.map(id => API.post(`/leverbon/${id}/verzend`, body)));
      setSelectedIds([]);
    } else {
      await API.post(`/leverbon/${verzendModal.did}/verzend`, body);
    }
    setVerzendModal(null);
    load();
    // ─── NIEUW: Vraag of klant op de hoogte gebracht moet worden via Mail/WhatsApp
    const leverbonnen = verzondenIds.map(id => {
      const d = docs.find(x => x.id === id);
      const k = d ? klanten.find(x => x.id === d.klant_id) : null;
      if (!d || !k) return null;
      const bedrijf = (k.bedrijf || '').trim();
      const persoon = `${k.voorn || ''} ${k.achtern || ''}`.trim();
      return {
        nummer: id,
        klant_id: k.id,
        klant_naam: bedrijf || persoon || k.naam || '(klant)',
        email: k.email || '',
        gsm: k.gsm || k.mobiel || k.tel || k.telefoon || '',
        taal: (k.taal || 'nl').toString().toLowerCase().slice(0, 2),
      };
    }).filter(Boolean);
    if (leverbonnen.length > 0) {
      setMeldVerzendModal({ leverbonnen, bedrijfsnaam: inst.naam || '' });
    }
  };

  const bulkMarkeerGeleverd = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} leverbon(nen) markeren als geleverd?`)) return;
    await Promise.all(selectedIds.map(id => API.post(`/leverbon/${id}/geleverd`)));
    setSelectedIds([]);
    load();
  };

  const bulkNaarFactuur = async () => {
    if (selectedIds.length < 1) return;
    // Check: zelfde klant?
    const selectedDocs = docs.filter(d => selectedIds.includes(d.id));
    const klantIds = [...new Set(selectedDocs.map(d => d.klant_id))];
    if (klantIds.length > 1) {
      alert('⚠️ Alle geselecteerde leverbonnen moeten van dezelfde klant zijn.');
      return;
    }
    const alGefactureerd = selectedDocs.filter(d => d.gefactureerd || d.factuur_id);
    if (alGefactureerd.length > 0) {
      alert(`⚠️ Deze leverbonnen zijn al gefactureerd: ${alGefactureerd.map(d => d.id).join(', ')}`);
      return;
    }
    const klantNaam = kNaam(klantIds[0]);
    if (!window.confirm(`${selectedIds.length} leverbon(nen) van ${klantNaam} samenvoegen tot 1 factuur?\n\nElke leverbon komt apart op de factuur met eigen sectie-header.`)) return;
    try {
      const res = await API.post('/leverbonnen/bundel-naar-factuur', { leverbon_ids: [...selectedIds] });
      setSelectedIds([]);
      alert(`✓ Factuur ${res.data.id} aangemaakt — totaal ${fmt(res.data.totaal)}`);
      navigate(`/documenten/${res.data.id}/bewerk`);
    } catch (e) {
      alert('❌ ' + (e?.response?.data?.detail || e.message || 'Mislukt'));
    }
  };

  const changeBetaalwijze = async (did, bw) => {
    await API.put(`/documenten/${did}`, { betaalwijze: bw });
    setDocs(prev => prev.map(d => d.id === did ? { ...d, betaalwijze: bw } : d));
  };

  const dupliceer = async (did) => {
    await API.post(`/documenten/${did}/dupliceer`);
    load();
  };

  const maakCreditnota = (did) => {
    // Open modal voor lijn-voor-lijn selectie i.p.v. directe volledige creditnota
    setPartialCnModal({ did });
  };

  const openVersturenModal = async (doc) => {
    setVersturenModal({ doc, opties: null, historiek: [], busy: true });
    try {
      const [optRes, histRes] = await Promise.all([
        API.get(`/documenten/${doc.id}/verzend-opties`),
        API.get(`/documenten/${doc.id}/historiek`),
      ]);
      setVersturenModal({ doc, opties: optRes.data, historiek: histRes.data || [], busy: false });
    } catch (e) {
      alert('Kan verzend-opties niet laden');
      setVersturenModal(null);
    }
  };

  const verstuurPeppol = async () => {
    if (!versturenModal?.doc) return;
    setVersturenModal(m => ({ ...m, busy: true }));
    try {
      const r = await API.post(`/documenten/${versturenModal.doc.id}/verzend-peppol`);
      alert(`Factuur verzonden via Peppol → ${r.data.participant}`);
      setVersturenModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Peppol verzenden mislukt');
      setVersturenModal(m => ({ ...m, busy: false }));
    }
  };

  const verstuurEmail = async () => {
    if (!versturenModal?.doc) return;
    const doc = versturenModal.doc;
    const k = kFull(doc.klant_id, doc);
    if (!k.email) { alert('Klant heeft geen e-mailadres'); return; }
    // Open MailPreviewModal voor controle vóór verzenden
    setPreviewDoc(doc);
    setVersturenModal(null);
  };

  const emailDoc = async (doc) => {
    const k = kFull(doc.klant_id, doc);
    if (!k.email) {
      alert('Geen e-mailadres gevonden voor deze klant. Voeg eerst een e-mail toe bij de klant.');
      return;
    }
    // Voor facturen/creditnota's: Versturen-modal (Peppol of e-mail) — die opent zelf de preview
    if (doc.type === 'factuur' || doc.type === 'creditnota') {
      openVersturenModal(doc);
      return;
    }
    // Voor offerte/proforma/bestelbon/leverbon: open MailPreviewModal direct (preview-popup)
    setPreviewDoc(doc);
  };

  const maakBetaallink = async (doc) => {
    try {
      const r = await API.post('/stripe/betaallink', { doc_id: doc.id, origin: window.location.origin });
      // Copy link or open
      if (r.data.url) {
        await navigator.clipboard.writeText(r.data.url).catch(() => {});
        const open = window.confirm(`Betaallink aangemaakt!\n\n${r.data.url}\n\nLink is gekopieerd. Openen in nieuw tabblad?`);
        if (open) window.open(r.data.url, '_blank');
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Betaallink aanmaken mislukt');
    }
  };

  // ── PDF Generation (Multi-layout) ──────────────────────────
  const getDefaultNota = (doc) => {
    const lang = doc.taal || localStorage.getItem('doc_lang') || 'nl';
    const notaMap = {
      factuur: { nl: 'factuur_nota_nl', fr: 'factuur_nota_fr', en: 'factuur_nota_en' },
      proforma: { nl: 'proforma_nota_nl', fr: 'proforma_nota_fr', en: 'proforma_nota_en' },
      creditnota: { nl: 'cn_nota_nl', fr: 'cn_nota_fr', en: 'cn_nota_en' },
    };
    const key = notaMap[doc.type]?.[lang];
    let tekst = key ? (inst[key] || '') : '';
    if (tekst) {
      tekst = tekst.replace(/{vervaldag}/g, doc.verval || '-')
        .replace(/{ogm}/g, doc.id || '')
        .replace(/{document_nr}/g, doc.id || '')
        .replace(/{bedrijfsnaam}/g, inst.naam || '');
    }
    return tekst;
  };

  const getKortingHtml = (doc) => {
    const dagen = inst.betalingskorting_dagen;
    const pct = inst.betalingskorting_pct;
    if (!dagen || !pct || doc.type !== 'factuur') return '';
    const kortBedrag = (doc.totaal || 0) * (1 - parseFloat(pct) / 100);
    return `<div style="margin-top:12px;padding:10px 14px;background:#FEF3C7;border:1px solid #FDE68A;font-size:11px;color:#92400E"><strong>Betalingskorting:</strong> Bij betaling binnen ${dagen} dagen ontvangt u ${pct}% korting. Te betalen: <strong>${fmt(kortBedrag)}</strong>. Na ${dagen} dagen vervalt de korting en betaalt u het volledige bedrag van ${fmt(doc.totaal)}.</div>`;
  };

  // ─── Professionele voettekst per documenttype ───────────────────
  const buildFooter = (doc) => {
    const iban = inst.iban || '';
    const bic = inst.bic || '';
    const bedrijf = inst.naam || '';
    const verval = doc.verval || '-';
    const totaal = fmt(doc.totaal || 0);
    const meded = doc.ogm || doc.id || '';

    // Vervang placeholders in custom tekst
    const applyPlaceholders = (txt) => (txt || '')
      .replace(/\{totaal\}/g, totaal)
      .replace(/\{vervaldag\}/g, verval)
      .replace(/\{ogm\}/g, meded)
      .replace(/\{iban\}/g, iban)
      .replace(/\{bic\}/g, bic)
      .replace(/\{bedrijf\}/g, bedrijf)
      .replace(/\{document_nr\}/g, doc.id || '')
      .replace(/\{datum\}/g, doc.datum || '');

    const cfg = {
      factuur: { key: 'factuur_voettekst', label: 'Betalingsinstructies', border: '#002FA7', bg: '#F0F4FF', textCol: '#002FA7' },
      offerte: { key: 'offerte_voettekst', label: 'Geldigheid', border: '#D97706', bg: '#FFFBEB', textCol: '#92400E' },
      creditnota: { key: 'cn_voettekst', label: 'Terugbetaling', border: '#DC2626', bg: '#FEF2F2', textCol: '#B91C1C' },
      bestelbon: { key: 'bestelbon_voettekst', label: 'Bestelvoorwaarden', border: '#059669', bg: '#ECFDF5', textCol: '#065F46' },
      leverbon: { key: 'leverbon_voettekst', label: 'Leveringsvoorwaarden', border: '#059669', bg: '#ECFDF5', textCol: '#065F46' },
    }[doc.type];

    const blocks = [];
    if (cfg) {
      const raw = inst[cfg.key];
      const body = raw !== undefined && raw !== '' ? applyPlaceholders(raw) : '';
      if (body) {
        // Voor facturen: highlight OGM automatisch in de tekst
        const decorated = doc.type === 'factuur' && meded
          ? body.replace(meded, `<span style="font-family:monospace;font-weight:700;color:#002FA7;letter-spacing:.8px;background:#fff;padding:1px 6px;border:1px solid #C7D2FE">${meded}</span>`)
          : body;
        blocks.push(
          `<div style="margin-top:24px;padding:14px 18px;background:${cfg.bg};border-left:3px solid ${cfg.border}">
            <div style="font-size:10px;font-weight:700;color:${cfg.textCol};text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px">${cfg.label}</div>
            <div style="font-size:12px;color:#1a1a1a;line-height:1.55">${decorated}</div>
            ${doc.type === 'factuur' && iban ? `<div style="font-size:11px;color:#4B5563;margin-top:8px;line-height:1.5">Rekening: <strong style="font-family:monospace">${iban}</strong>${bic ? ` &middot; BIC: <span style="font-family:monospace">${bic}</span>` : ''}${bedrijf ? ` &middot; Begunstigde: ${bedrijf}` : ''}</div>` : ''}
          </div>`
        );
      }
    }

    // Bestel-/leveringsbon: handtekeningvelden (extra, altijd)
    if (doc.type === 'leverbon' || doc.type === 'bestelbon') {
      blocks.push(
        `<div style="margin-top:24px;padding-top:14px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:11px;color:#6b7280">
          <div><strong style="color:#111827">Handtekening klant</strong><br/><span style="color:#9ca3af">Voor ${doc.type === 'leverbon' ? 'ontvangst' : 'bestelling'}</span><br/><br/><br/>____________________________</div>
          <div style="text-align:right"><strong style="color:#111827">Handtekening ${bedrijf || ''}</strong><br/><span style="color:#9ca3af">Datum: ${doc.datum || '-'}</span><br/><br/><br/>____________________________</div>
        </div>`
      );
    }

    // Bedrijfs-compliance regel onderaan (altijd)
    const footerParts = [
      bedrijf,
      inst.adres,
      inst.btw ? `BTW: ${inst.btw}` : '',
      iban ? `IBAN: ${iban}` : '',
      inst.email,
      inst.tel,
    ].filter(Boolean);
    if (footerParts.length > 0) {
      blocks.push(
        `<div style="margin-top:28px;padding-top:10px;border-top:1px solid #e5e7eb;font-size:9px;color:#9ca3af;text-align:center;line-height:1.55">${footerParts.join(' &middot; ')}</div>`
      );
    }

    return blocks.join('');
  };


  // ── PDF open/download via backend (premium layout uit PDF-layout editor) ──
  // Centrale flow: iOS Safari gebruikt server-URL (geen blob), desktop blob in nieuwe tab.
  const generatePdf = async (doc) => {
    try {
      const k = kFull(doc.klant_id, doc);
      const keuze = await vraagFotoKeuze(API, doc.type);
      const prijs = await vraagPrijsKeuze(doc.type);
      const params = { toon_fotos: keuze };
      if (prijs !== null) params.met_prijzen = prijs;
      const actions = await fetchPdfActions({
        endpoint: `/documenten/${doc.id}/pdf`,
        label: pdfFilename(doc, k).replace(/\.pdf$/, ''),
        params,
        filename: pdfFilename(doc, k),
      });
      openPdfHelper(actions);
    } catch (e) {
      alert('PDF-generatie mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const downloadPdf = async (doc) => {
    try {
      const k = kFull(doc.klant_id, doc);
      const keuze = await vraagFotoKeuze(API, doc.type);
      const prijs = await vraagPrijsKeuze(doc.type);
      const params = { toon_fotos: keuze };
      if (prijs !== null) params.met_prijzen = prijs;
      const actions = await fetchPdfActions({
        endpoint: `/documenten/${doc.id}/pdf`,
        label: pdfFilename(doc, k).replace(/\.pdf$/, ''),
        params,
        filename: pdfFilename(doc, k),
      });
      // iOS: openen (gebruiker kan vandaar opslaan via native share-sheet)
      // Desktop: trigger download
      if (isIOSPlatform()) openPdfHelper(actions); else downloadPdfHelper(actions);
    } catch (e) {
      alert('Download mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Optionele nieuwe actie: deel PDF via native share-sheet (WhatsApp/Mail/AirDrop)
  const sharePdfDoc = async (doc) => {
    try {
      const k = kFull(doc.klant_id, doc);
      const keuze = await vraagFotoKeuze(API, doc.type);
      const prijs = await vraagPrijsKeuze(doc.type);
      const params = { toon_fotos: keuze };
      if (prijs !== null) params.met_prijzen = prijs;
      const actions = await fetchPdfActions({
        endpoint: `/documenten/${doc.id}/pdf`,
        label: pdfFilename(doc, k).replace(/\.pdf$/, ''),
        params,
        filename: pdfFilename(doc, k),
      });
      await sharePdfHelper(actions, { onError: (err) => alert('Delen mislukt: ' + err.message) });
    } catch (e) {
      alert('Delen mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Nieuwe actie: deel PDF DIRECT via WhatsApp (deep-link naar wa.me).
  // Verschil met sharePdfDoc: opent onmiddellijk WhatsApp met de link i.p.v. native share-sheet.
  // De temp-PDF-link wordt automatisch verlengd naar 24u zodat de ontvanger tijd heeft om te openen.
  const whatsappPdfDoc = async (doc) => {
    try {
      const k = kFull(doc.klant_id, doc);
      const keuze = await vraagFotoKeuze(API, doc.type);
      const prijs = await vraagPrijsKeuze(doc.type);
      const params = { toon_fotos: keuze };
      if (prijs !== null) params.met_prijzen = prijs;
      const actions = await fetchPdfActions({
        endpoint: `/documenten/${doc.id}/pdf`,
        label: pdfFilename(doc, k).replace(/\.pdf$/, ''),
        params,
        filename: pdfFilename(doc, k),
      });
      // Pre-fill met klant-telefoon indien beschikbaar (WhatsApp neemt eerste geldig nummer)
      const klantTel = k?.tel || k?.gsm || k?.telefoon || '';
      const result = await whatsAppPdfHelper(actions, { ttlHours: 24, telefoon: klantTel });
      if (result === 'no-link') {
        // Fallback: native share-sheet (bevat WhatsApp meestal ook)
        await sharePdfHelper(actions, { onError: (err) => alert('Delen mislukt: ' + err.message) });
      }
    } catch (e) {
      alert('WhatsApp delen mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };


  // KPIs
  const counts = {};
  cfg.statuses.forEach(s => { counts[s] = typeDocs.filter(d => d.status === s).length; });
  const teFacturerenCount = cfg.actionStatus ? typeDocs.filter(d => d.status === cfg.actionStatus && !d.gefactureerd).length : 0;
  const teFacturerenBedrag = cfg.actionStatus ? typeDocs.filter(d => d.status === cfg.actionStatus && !d.gefactureerd).reduce((s, d) => s + (d.totaal || 0), 0) : 0;

  // ─── Productie-overzicht (enkel bestelbon): documenten + artikel-samenvatting voor status 'productie in afwachting' ───
  const productieDocs = docType === 'bestelbon' ? typeDocs.filter(d => (d.status || '').toLowerCase() === 'productie in afwachting') : [];
  const productieArtikelen = (() => {
    const map = {};
    productieDocs.forEach(d => {
      (d.lijnen || []).forEach(l => {
        const key = l.referentie || l.omschr || 'onbekend';
        if (!map[key]) map[key] = { referentie: l.referentie || '', omschr: l.omschr || '', eenheid: l.eenheid_label || l.eenheid || '', aantal: 0, orderIds: new Set() };
        map[key].aantal += Number(l.aantal || 0);
        map[key].orderIds.add(d.id);
      });
    });
    return Object.values(map).map(a => ({ ...a, orders: a.orderIds.size })).sort((a, b) => b.aantal - a.aantal);
  })();
  const dagenInProductie = (datum) => {
    if (!datum) return null;
    const diff = Math.floor((Date.now() - new Date(datum).getTime()) / 86400000);
    return diff >= 0 ? diff : 0;
  };

  // ─── Bedrag-KPIs voor Facturen: Totaal omzet, Ontvangen, Te ontvangen ───
  // Concept, geannuleerd en geblokkeerd worden UITGESLOTEN uit omzet-cijfers
  const factuurBedragen = (() => {
    if (docType !== 'factuur') return null;
    let omzet = 0, betaald = 0, teOntvangen = 0;
    typeDocs.forEach(d => {
      if (d.status === 'concept' || d.status === 'geannuleerd' || d.status === 'geblokkeerd') return;
      const tot = Number(d.totaal || 0);
      const cn = Math.abs(Number(d.creditnota_totaal || 0));
      const bet = Number(d.betaald_bedrag || 0);
      const netto = Math.max(0, tot - cn); // netto omzet na creditnota
      omzet += netto;
      betaald += Math.min(bet, netto); // betaald kan niet meer zijn dan netto
      teOntvangen += Math.max(0, netto - bet);
    });
    return { omzet, betaald, teOntvangen };
  })();

  // ─── Toegangscontrole ────────────────────────────────────────────
  // Alle hooks moeten hierboven staan (React hooks-regel). Pas ná alle
  // hooks tonen we optioneel het "Geen toegang"-scherm.
  const _isAdmin = !user?.role || user?.role === 'admin';
  const _macht = user?.machtigingen || {};
  const _requiredPerm = DOCTYPE_PERMKEY[docType];
  const _heeftToegang = _isAdmin || !_requiredPerm || !!_macht[_requiredPerm];
  if (!_heeftToegang) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="no-access">
        <div className="text-5xl mb-4">🔒</div>
        <div className="text-lg font-bold text-gray-900 mb-2">Geen toegang</div>
        <div className="text-sm text-gray-600 mb-6">
          U heeft geen machtiging om <strong>{cfg.label}</strong> te bekijken.
          Vraag uw beheerder om de machtiging <code className="bg-gray-100 px-1 rounded">{_requiredPerm}</code> toe te kennen.
        </div>
        <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-[#002FA7] text-white text-sm font-bold hover:bg-[#001E6E]" data-testid="back-dashboard-btn">
          Terug naar dashboard
        </button>
      </div>
    );
  }

  return (
    <div className={docType === 'factuur' ? 'invoice-list' : undefined} data-testid={`${docType}-page`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="document-list-header">
        <div className="min-w-0 max-w-full break-words">
          <h1 className="page-heading mb-0">{titleOverride || cfg.label}</h1>
          {subtitleOverride && <p className="text-xs text-gray-500 mt-1">{subtitleOverride}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 max-w-full" data-testid="document-header-actions">
          <button
            className="btn btn-secondary inline-flex items-center gap-1.5 px-2.5"
            onClick={() => setDensity(d => { const nx = d === 'compact' ? 'comfortable' : 'compact'; localStorage.setItem('bf_table_density', nx); return nx; })}
            data-testid="table-density-toggle"
            title={density === 'compact' ? 'Wissel naar ruime tabelweergave' : 'Wissel naar compacte tabelweergave'}
          >
            {density === 'compact' ? <ArrowsOut size={14} weight="bold" /> : <ArrowsIn size={14} weight="bold" />}
            {density === 'compact' ? 'Ruim' : 'Compact'}
          </button>
          <button
            className="btn btn-secondary inline-flex items-center gap-1.5"
            onClick={openExcelModal}
            data-testid="excel-export-btn"
            title="Exporteer documenten naar Excel (datumbereik + filters)"
          >
            <DownloadSimple size={14} weight="bold" /> Excel-export
          </button>
          {bronFilter !== 'shop' && (
            <button className="btn btn-primary" onClick={() => navigate(`/documenten/nieuw?type=${docType}`)} data-testid={`new-${docType}-btn`}>
              <Plus size={14} weight="bold" /> Nieuwe {cfg.single}
            </button>
          )}
        </div>
      </div>

      {/* Bedrag-KPIs voor Facturen — Totaal omzet + Ontvangen + Te ontvangen (klikbare drilldown) */}
      {factuurBedragen && (
        <div className="document-summary-grid document-summary-amounts" data-testid="document-amount-summaries">
          <DocumentSummary label="Totaal omzet" value={fmt(factuurBedragen.omzet)} detail="Netto na creditnota" color="#002FA7"
            selected={statusF === '__omzet__'} onClick={() => setStatusF(statusF === '__omzet__' ? '' : '__omzet__')}
            testId="kpi-factuur-omzet" title="Alle niet-concept facturen" />
          <DocumentSummary label="Ontvangen" value={fmt(factuurBedragen.betaald)} detail="Reeds geïnd" color="#008A27"
            selected={statusF === '__ontvangen__'} onClick={() => setStatusF(statusF === '__ontvangen__' ? '' : '__ontvangen__')}
            testId="kpi-factuur-betaald" title="Betaalde en gedeeltelijk betaalde facturen" />
          <DocumentSummary label="Te ontvangen" value={fmt(factuurBedragen.teOntvangen)} detail="Openstaand saldo" color="#A16207"
            selected={statusF === '__te_ontvangen__'} onClick={() => setStatusF(statusF === '__te_ontvangen__' ? '' : '__te_ontvangen__')}
            testId="kpi-factuur-te-ontvangen" title="Openstaande, gedeeltelijk betaalde en vervallen facturen" />
        </div>
      )}

      {/* KPIs */}
      <div className="document-summary-grid" data-testid="document-status-summaries">
        <DocumentSummary label="Totaal" value={typeDocs.length} testId="kpi-document-total" />
        {cfg.statuses.map(s => (
          <DocumentSummary key={s} label={STATUS_PILL[s]?.label || s} value={counts[s] || 0}
            color={['betaald', 'goedgekeurd', 'geleverd', 'besteld'].includes(s) ? '#008A27' : ['vervallen', 'afgewezen'].includes(s) ? '#E02424' : ['openstaand', 'gedeeltelijk betaald'].includes(s) ? '#A16207' : undefined}
            onClick={() => setStatusF(statusF === s ? '' : s)} selected={statusF === s} testId={`kpi-status-${s.replaceAll(' ', '-')}`} />
        ))}
        {cfg.actionStatus && teFacturerenCount > 0 && (
          <DocumentSummary label="Te factureren" value={teFacturerenCount} detail={fmt(teFacturerenBedrag)} color="#C2410C"
            selected={statusF === '__te_factureren__'} onClick={() => setStatusF(statusF === '__te_factureren__' ? '' : '__te_factureren__')} testId="kpi-te-factureren" />
        )}
      </div>

      {/* Tabs + Filters */}

      {docType === 'bestelbon' && (
        <div className="flex gap-3 mb-3 items-center" data-testid="bestelbon-tabs">
          <button className={`text-sm font-bold ${statusF !== 'productie in afwachting' ? 'text-[#002FA7] border-b-2 border-[#002FA7]' : 'text-gray-400 hover:text-gray-600'} pb-1`}
            onClick={() => setStatusF('')} data-testid="tab-alle-bestelbonnen">Alle</button>
          <button className={`text-sm font-bold ${statusF === 'productie in afwachting' ? 'text-[#7C3AED] border-b-2 border-[#7C3AED]' : 'text-gray-400 hover:text-gray-600'} pb-1 flex items-center gap-1`}
            onClick={() => setStatusF(statusF === 'productie in afwachting' ? '' : 'productie in afwachting')} data-testid="tab-productie-overzicht">
            ⚙ Productie overzicht {counts['productie in afwachting'] > 0 && <span className="bg-[#7C3AED] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{counts['productie in afwachting']}</span>}
          </button>
        </div>
      )}

      {docType === 'bestelbon' && statusF === 'productie in afwachting' && (
        <div className="mb-5 border border-[#7C3AED]/30 bg-[#F5F3FF] p-4" data-testid="productie-overzicht-artikelen">
          <div className="text-[11px] font-black uppercase tracking-wider text-[#5B21B6] mb-3">
            ⚙ Artikelen in productie — {productieDocs.length} bestelbon{productieDocs.length !== 1 ? 'nen' : ''}, {productieArtikelen.length} verschillende artikelen
          </div>
          {productieArtikelen.length === 0 ? (
            <div className="text-xs text-gray-500">Geen bestelbonnen momenteel "in productie".</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#5B21B6] border-b border-[#7C3AED]/30">
                    <th className="py-1.5 pr-3">Artikel</th>
                    <th className="py-1.5 pr-3">Omschrijving</th>
                    <th className="py-1.5 pr-3 text-right">Aantal</th>
                    <th className="py-1.5 pr-3">Eenheid</th>
                    <th className="py-1.5 pr-3 text-right"># Bestelbonnen</th>
                  </tr>
                </thead>
                <tbody>
                  {productieArtikelen.map((a, i) => (
                    <tr key={a.referentie || i} className="border-b border-[#7C3AED]/10" data-testid={`productie-artikel-row-${i}`}>
                      <td className="py-1.5 pr-3 font-mono font-bold">{a.referentie || '—'}</td>
                      <td className="py-1.5 pr-3">{a.omschr || '—'}</td>
                      <td className="py-1.5 pr-3 text-right font-bold">{a.aantal}</td>
                      <td className="py-1.5 pr-3 text-gray-500">{a.eenheid || '—'}</td>
                      <td className="py-1.5 pr-3 text-right">{a.orders}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-[11px] font-black uppercase tracking-wider text-[#5B21B6] mt-5 mb-2">Bestelbonnen in productie</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#5B21B6] border-b border-[#7C3AED]/30">
                  <th className="py-1.5 pr-3">Nr</th>
                  <th className="py-1.5 pr-3">Klant</th>
                  <th className="py-1.5 pr-3">Verkoper</th>
                  <th className="py-1.5 pr-3">Datum</th>
                  <th className="py-1.5 pr-3 text-right">Dagen in productie</th>
                  <th className="py-1.5 pr-3 text-right">Totaal</th>
                </tr>
              </thead>
              <tbody>
                {productieDocs.map(d => {
                  const dagen = dagenInProductie(d.datum);
                  return (
                    <tr key={d.id} className="border-b border-[#7C3AED]/10 cursor-pointer hover:bg-white" onClick={() => navigate(`/documenten/${d.id}/bewerk?type=bestelbon`)} data-testid={`productie-doc-row-${d.id}`}>
                      <td className="py-1.5 pr-3 font-bold text-[#002FA7]">{d.nummer || d.id}</td>
                      <td className="py-1.5 pr-3">{kNaam(d.klant_id, d)}</td>
                      <td className="py-1.5 pr-3">{d.verkoper_naam || '—'}</td>
                      <td className="py-1.5 pr-3">{d.datum || '—'}</td>
                      <td className="py-1.5 pr-3 text-right">{dagen !== null ? `${dagen} d.` : '—'}</td>
                      <td className="py-1.5 pr-3 text-right font-bold">€ {(d.totaal || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <div className="relative" style={{ maxWidth: 250 }}>
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input className="field-input" style={{ paddingLeft: 36 }} placeholder="Zoek op klant of nummer..." value={q} onChange={e => setQ(e.target.value)} data-testid={`${docType}-search`} />
        </div>
        {docType !== 'factuur' && (
          <select className="field-input" style={{ maxWidth: 180 }} value={statusF.startsWith('__') ? '' : statusF} onChange={e => setStatusF(e.target.value)} data-testid={`${docType}-status-filter`}>
            <option value="">Alle statussen</option>
            {cfg.statuses.map(s => <option key={s} value={s}>{s}</option>)}
            {cfg.actionStatus && cfg.actionLabel && (
              <option value="__te_factureren__">{`🧾 ${cfg.actionLabel}`}</option>
            )}
          </select>
        )}
        {docType === 'factuur' && (
          <select className="field-input" style={{ maxWidth: 180 }} value={statusF} onChange={e => setStatusF(e.target.value)} data-testid="factuur-status-filter" aria-label="Factuurstatus filteren">
            <option value="">Alle statussen</option>
            {statusF.startsWith('__') && <option value={statusF}>{({ __omzet__: 'Totaal omzet', __ontvangen__: 'Ontvangen', __te_ontvangen__: 'Te ontvangen' })[statusF] || 'Bedragfilter'}</option>}
            {cfg.statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {/* Actieve synthetische filter → toon als verwijderbare badge */}
        {statusF.startsWith('__') && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#EEF2FF] border border-[#002FA7] text-[#002FA7] text-[11px] font-semibold rounded" data-testid="active-synthetic-filter">
            {statusF === '__te_factureren__' && '🧾 Te factureren'}
            {statusF === '__te_ontvangen__' && '⏳ Te ontvangen'}
            {statusF === '__ontvangen__' && '✓ Ontvangen'}
            {statusF === '__omzet__' && '💰 Alle omzet'}
            <button className="ml-1 hover:text-[#E02424]" onClick={() => setStatusF('')} title="Filter verwijderen">✕</button>
          </span>
        )}
        {statusF && !statusF.startsWith('__') && <button className="btn btn-ghost btn-sm text-xs" onClick={() => setStatusF('')}>Filter wissen</button>}
        {docType === 'bestelbon' && verkopers.length > 0 && (
          <select className="field-input" style={{ maxWidth: 200 }} value={verkoperF} onChange={e => setVerkoperF(e.target.value)} data-testid="bestelbon-verkoper-filter">
            <option value="">Alle verkopers</option>
            {verkopers.map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
          </select>
        )}
        {verkoperF && <button className="btn btn-ghost btn-sm text-xs" onClick={() => setVerkoperF('')}>Verkoper wissen</button>}
        {/* Datum filter van/tot */}
        <div className="inline-flex flex-wrap max-w-full items-center gap-2 border border-gray-200 px-2 py-1 bg-gray-50" data-testid={`${docType}-date-range`}>
          <label className="inline-flex items-center gap-1 min-w-0">
            <span className="text-[10px] uppercase font-bold text-gray-500">Van</span>
            <input type="date" className="field-input text-xs h-7" style={{ width: 130 }} value={datumVan} onChange={e => setDatumVan(e.target.value)} data-testid={`${docType}-datum-van`} />
          </label>
          <label className="inline-flex items-center gap-1 min-w-0">
            <span className="text-[10px] uppercase font-bold text-gray-500">Tot</span>
            <input type="date" className="field-input text-xs h-7" style={{ width: 130 }} value={datumTot} onChange={e => setDatumTot(e.target.value)} data-testid={`${docType}-datum-tot`} />
          </label>
          {(datumVan || datumTot) && (
            <button className="text-[10px] text-red-600 hover:underline ml-1" onClick={() => { setDatumVan(''); setDatumTot(''); }} data-testid={`${docType}-datum-wissen`}>×</button>
          )}
        </div>
        {/* Snelkoppelingen voor datum */}
        <div className="inline-flex gap-1">
          <button type="button" className="text-[10px] px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 border border-gray-200" onClick={() => {
            const t = new Date(); const m = String(t.getMonth() + 1).padStart(2, '0'); const y = t.getFullYear();
            setDatumVan(`${y}-${m}-01`);
            setDatumTot(`${y}-${m}-${String(new Date(y, t.getMonth() + 1, 0).getDate()).padStart(2, '0')}`);
          }}>Deze maand</button>
          <button type="button" className="text-[10px] px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 border border-gray-200" onClick={() => {
            const t = new Date(); const prev = new Date(t.getFullYear(), t.getMonth() - 1, 1);
            const py = prev.getFullYear(); const pm = String(prev.getMonth() + 1).padStart(2, '0');
            const last = new Date(py, prev.getMonth() + 1, 0).getDate();
            setDatumVan(`${py}-${pm}-01`);
            setDatumTot(`${py}-${pm}-${String(last).padStart(2, '0')}`);
          }}>Vorige</button>
          <button type="button" className="text-[10px] px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 border border-gray-200" onClick={() => {
            const y = new Date().getFullYear();
            setDatumVan(`${y}-01-01`); setDatumTot(`${y}-12-31`);
          }}>Dit jaar</button>
        </div>
        <a href="/instellingen?tab=pdf_layouts" className="text-[11px] text-[#002FA7] hover:underline ml-2" title="Pas kleuren, kolommen en layout per documenttype aan">
          📄 PDF-layouts bewerken
        </a>
        <div className="ml-auto text-sm text-gray-400">{filtered.length} van {typeDocs.length}</div>
      </div>

      {/* Bulk action bar - ALTIJD zichtbaar voor alle document-types, disabled zonder selectie */}
      {(() => {
        const N = selectedIds.length;
        const disabled = N === 0;
        const disCls = 'opacity-40 cursor-not-allowed pointer-events-none';
        return (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-[#EEF3FF] border border-[#002FA7]/30 flex-wrap" data-testid="bulk-action-bar">
          <span className={`text-sm font-bold ${disabled ? 'text-gray-400' : 'text-[#002FA7]'}`}>
            {disabled ? 'Geen selectie' : `${N} geselecteerd`}
          </span>
          <span className="text-xs text-gray-500">—</span>

          {/* Verzenden — alleen voor leverbon-specifieke modal */}
          {docType === 'leverbon' && (
            <button
              className={`btn btn-primary btn-sm inline-flex items-center gap-1.5 ${disabled ? disCls : ''}`}
              onClick={() => !disabled && setVerzendModal({ ids: [...selectedIds], chauffeur_id: '' })}
              disabled={disabled}
              data-testid="bulk-verzend-btn"
            >
              🚚 Verzenden ({N})
            </button>
          )}
          {docType !== 'leverbon' && (
            <button
              className={`btn btn-primary btn-sm inline-flex items-center gap-1.5 ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={async () => {
                if (disabled) return;
                if (!window.confirm(`${N} ${(TYPE_CFG[docType]?.single || 'document').toLowerCase()}(en) verzenden per e-mail?`)) return;
                let ok = 0, fail = 0;
                for (const id of selectedIds) {
                  try { await API.post(`/documenten/${id}/mail`, {}); ok++; } catch { fail++; }
                }
                setSelectedIds([]);
                alert(`Verzonden: ${ok} ✓${fail ? ` · Mislukt: ${fail}` : ''}`);
                load();
              }}
              data-testid="bulk-verzend-btn"
            >
              ✉️ Verzenden per e-mail ({N})
            </button>
          )}

          {docType === 'leverbon' && <>
            <button
              className={`btn btn-sm inline-flex items-center gap-1.5 bg-[#008A27] text-white hover:bg-[#006e1f] ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={() => !disabled && bulkMarkeerGeleverd()}
              data-testid="bulk-geleverd-btn"
            >
              ✓ Geleverd markeren ({N})
            </button>
            <button
              className={`btn btn-sm inline-flex items-center gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={() => !disabled && setChauffeurAdresModal({ leverbonIds: [...selectedIds] })}
              data-testid="bulk-chauffeur-btn"
              title="Stuur alle geselecteerde leveringsadressen samengevat in 1 WhatsApp-bericht naar de chauffeur"
            >
              📱 Naar chauffeur ({N})
            </button>
            <button
              className={`btn btn-sm inline-flex items-center gap-1.5 bg-[#1a1a1a] text-white hover:bg-black ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={() => !disabled && setEtiketModal({ docIds: [...selectedIds] })}
              data-testid="bulk-etiket-btn"
              title="Adresetiketten voor alle geselecteerde leverbonnen in 1 A4-pdf (2 per blad)"
            >
              🏷️ Adresetiketten ({N})
            </button>
            <button
              className={`btn btn-sm inline-flex items-center gap-1.5 bg-[#7C3AED] text-white hover:bg-[#6D28D9] ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={() => !disabled && bulkNaarFactuur()}
              data-testid="bulk-factuur-btn"
              title="Bundel alle geselecteerde leverbonnen tot 1 factuur (zelfde klant vereist)"
            >
              📄 Factureren ({N})
            </button>
          </>}

          {docType === 'factuur' && (
            <button
              className={`btn btn-sm inline-flex items-center gap-1.5 bg-[#B91C1C] text-white hover:bg-[#991B1B] ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                const selectedDocs = docs.filter(d => selectedIds.includes(d.id));
                const klantIds = [...new Set(selectedDocs.map(d => d.klant_id))];
                if (klantIds.length !== 1 || !klantIds[0]) {
                  alert('Selecteer facturen van 1 klant om samen een betalingsherinnering op te maken.');
                  return;
                }
                setBhModal({ klantId: klantIds[0], ids: [...selectedIds] });
              }}
              data-testid="bulk-betalingsherinnering-btn"
              title="Maak 1 betalingsherinnering op voor de geselecteerde facturen (zelfde klant vereist)"
            >
              ⚠️ Betalingsherinnering ({N})
            </button>
          )}

          {docType === 'factuur' && (
            <button
              className={`btn btn-sm inline-flex items-center gap-1.5 bg-[#008A27] text-white hover:bg-[#006e1f] ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={async () => {
                if (disabled) return;
                if (!window.confirm(`${N} factu${N > 1 ? 'ren' : 'ur'} markeren als volledig betaald (met datum = vandaag, wijze = Overschrijving)?`)) return;
                const today = new Date().toISOString().slice(0, 10);
                let ok = 0, fail = 0;
                for (const id of selectedIds) {
                  try {
                    await API.post(`/documenten/${id}/markeer-betaald`, { datum: today, wijze: 'Overschrijving' });
                    ok++;
                  } catch { fail++; }
                }
                setSelectedIds([]);
                alert(`Gemarkeerd als betaald: ${ok} ✓${fail ? ` · Mislukt: ${fail}` : ''}`);
                load();
              }}
              data-testid="bulk-betaald-btn"
            >
              💶 Markeer als betaald ({N})
            </button>
          )}

          {/* Status wijzigen dropdown - voor alle types */}
          <div className="relative" style={{ display: 'inline-block' }}>
            <select
              className="btn btn-sm inline-flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer"
              disabled={disabled}
              defaultValue=""
              onChange={async (e) => {
                const newStatus = e.target.value;
                e.target.value = '';  // reset
                if (!newStatus || disabled) return;
                if (!window.confirm(`Status van ${N} document(en) wijzigen naar "${newStatus}"?`)) return;
                let ok = 0, fail = 0;
                for (const id of selectedIds) {
                  try { await API.put(`/documenten/${id}`, { status: newStatus }); ok++; } catch { fail++; }
                }
                setSelectedIds([]);
                alert(`Status gewijzigd: ${ok} ✓${fail ? ` · Mislukt: ${fail}` : ''}`);
                load();
              }}
              data-testid="bulk-status-select"
              style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
            >
              <option value="">🔀 Status wijzigen...</option>
              {(TYPE_CFG[docType]?.statuses || []).map(s => (
                <option key={s} value={s}>{`→ ${s.charAt(0).toUpperCase() + s.slice(1)}`}</option>
              ))}
            </select>
          </div>

          <button
            className={`btn btn-sm inline-flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 ${disabled ? disCls : ''}`}
            disabled={disabled}
            onClick={async () => {
              if (disabled) return;
              // Eenmaal vragen voor alle geselecteerde PDF's (gebruikt huidige tab-type)
              const keuze = await vraagFotoKeuze(API, docType);
              const prijs = await vraagPrijsKeuze(docType);
              for (const id of selectedIds) {
                let url = appendFotoParam(`${process.env.REACT_APP_BACKEND_URL}/api/documenten/${id}/pdf`, keuze);
                if (prijs !== null) url += `&met_prijzen=${prijs}`;
                window.open(url, '_blank');
              }
            }}
            data-testid="bulk-pdf-btn"
          >
            📄 PDF's openen ({N})
          </button>

          {/* NIEUW: PDF-ZIP-export — alle PDFs gebundeld in één zip-bestand */}
          <button
            className={`btn btn-sm inline-flex items-center gap-1.5 bg-[#4F46E5] text-white hover:bg-[#4338CA] ${disabled ? disCls : ''}`}
            disabled={disabled}
            onClick={async () => {
              if (disabled) return;
              try {
                const r = await API.post('/documenten/bulk-actie', { actie: 'exporteer_zip', doc_ids: selectedIds }, { responseType: 'blob' });
                const blob = new Blob([r.data], { type: 'application/zip' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url;
                a.download = `${docType}-export-${new Date().toISOString().slice(0, 10)}.zip`;
                a.click(); URL.revokeObjectURL(url);
                setSelectedIds([]);
              } catch (e) { alert('ZIP-export mislukt: ' + (e.response?.data?.detail || e.message)); }
            }}
            data-testid="bulk-zip-btn"
            title="Download alle geselecteerde PDFs in één zip-bestand"
          >
            📦 PDF-ZIP ({N})
          </button>

          {/* Bulk UBL/Peppol-XML export — wettelijk verplicht formaat voor B2B-facturen */}
          {(docType === 'factuur' || docType === 'creditnota') && (
            <button
              className={`btn btn-sm inline-flex items-center gap-1.5 bg-[#059669] text-white hover:bg-[#047857] ${disabled ? disCls : ''}`}
              disabled={disabled}
              onClick={async () => {
                if (disabled) return;
                try {
                  const r = await API.post('/documenten/bulk-actie', { actie: 'exporteer_ubl_zip', doc_ids: selectedIds }, { responseType: 'blob' });
                  const blob = new Blob([r.data], { type: 'application/zip' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url;
                  a.download = `peppol-ubl-${new Date().toISOString().slice(0, 10)}.zip`;
                  a.click(); URL.revokeObjectURL(url);
                  setSelectedIds([]);
                } catch (e) { alert('UBL-ZIP export mislukt: ' + (e.response?.data?.detail || e.message)); }
              }}
              data-testid="bulk-ubl-btn"
              title="Download alle UBL 2.1 / Peppol BIS 3.0 XML-bestanden in één zip"
            >
              📄 Peppol UBL-ZIP ({N})
            </button>
          )}

          <button
            className={`btn btn-sm inline-flex items-center gap-1.5 bg-white border border-red-300 text-red-700 hover:bg-red-50 ${disabled ? disCls : ''}`}
            disabled={disabled}
            onClick={async () => {
              if (disabled) return;
              const ok = await confirm({
                title: `${N} document(en) verwijderen`,
                subtitle: 'Definitief verwijderen — niet omkeerbaar.',
                message: `Je staat op het punt om ${N} document(en) te verwijderen. Deze actie kan niet ongedaan worden gemaakt.`,
                tone: 'danger',
                confirmText: `Verwijder ${N}`,
                confirmWord: N >= 5 ? 'VERWIJDER' : null,
              });
              if (!ok) return;
              let okC = 0, fail = 0;
              for (const id of selectedIds) {
                try { await API.delete(`/documenten/${id}`); okC++; } catch { fail++; }
              }
              setSelectedIds([]);
              alert(`Verwijderd: ${okC}${fail ? ` · Mislukt: ${fail}` : ''}`);
              load();
            }}
            data-testid="bulk-delete-btn"
          >
            🗑️ Verwijderen ({N})
          </button>

          <button
            className={`btn btn-ghost btn-sm text-xs ml-auto ${disabled ? disCls : ''}`}
            disabled={disabled}
            onClick={() => setSelectedIds([])}
            data-testid="bulk-clear-btn"
          >
            Selectie wissen
          </button>
        </div>
        );
      })()}

      {/* Table */}
      <div className="section-card">
        <div className="section-body" style={{ overflowX: 'auto' }}>
          <table className={`data-table ${docType === 'factuur' ? 'invoice-table' : docType === 'leverbon' ? 'delivery-note-table' : ''} ${density === 'comfortable' ? 'density-comfortable' : ''}`}
            style={{ width: '100%', tableLayout: docType === 'leverbon' ? 'fixed' : 'auto', fontSize: 13,
              ...(docType === 'leverbon' ? { '--delivery-min-width': winstMap !== null ? '1584px' : '1454px', minWidth: 'calc(var(--delivery-min-width) + var(--delivery-touch-extra, 0px))' } : {}) }} data-testid={`${docType}-table`}>
            {docType === 'leverbon' && <colgroup>
              <col style={{ width: 32 }} /><col style={{ width: 136 }} /><col style={{ width: 96 }} /><col style={{ width: 200 }} />
              <col style={{ width: 100 }} /><col style={{ width: 84 }} />
              <col style={{ width: 112 }} />{winstMap !== null && <col style={{ width: 130 }} />}
              <col style={{ width: 164 }} /><col />
            </colgroup>}
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selectedIds.length === filtered.length}
                    onChange={e => setSelectedIds(e.target.checked ? filtered.map(x => x.id) : [])}
                    data-testid="bulk-select-all"
                    title="Alles (de)selecteren"
                  />
                </th>
                <th>{docType === 'factuur' ? 'Factuurnr.' : 'Nr'}</th>
                <th>Datum</th>
                {docType === 'factuur' && <th>Vervaldatum</th>}
                <th className={docType === 'factuur' ? 'invoice-client-header' : undefined}>Klant</th>
                {docType === 'bestelbon' && <th>Verkoper</th>}
                {docType === 'factuur' && winstMap !== null && <th className="right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1" data-testid="toggle-winst-kolom"><span>Winst</span>
                    <IOSToggle checked={winstKolom} onChange={() => setWinstKolom(v => { const n = !v; try { localStorage.setItem('doclist_winst_kolom', n ? '1' : '0'); } catch {} return n; })} size="sm" testid="invoice-profit-toggle" ariaLabel="Winst tonen" />
                  </div>
                </th>}
                {['factuur', 'leverbon'].includes(docType) && <><th className="right" data-testid={`${docType}-excl-header`}>Excl.</th><th className="right" data-testid={`${docType}-vat-header`}>Btw</th></>}
                <th className="right" data-testid={`${docType}-total-header`}>Totaal</th>
                {docType !== 'factuur' && winstMap !== null && (
                  <th className="right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5" data-testid="toggle-winst-kolom">
                      <span>Winst</span>
                      <IOSToggle checked={winstKolom} onChange={() => setWinstKolom(v => { const n = !v; try { localStorage.setItem('doclist_winst_kolom', n ? '1' : '0'); } catch { /* quota */ } return n; })} size="sm" />
                    </div>
                  </th>
                )}
                {docType === 'factuur' && <th className="right whitespace-nowrap">Te betalen</th>}
                <th style={{ width: docType === 'factuur' ? 194 : docType === 'leverbon' ? 164 : 188 }}>Status</th>
                <th style={{ width: docType === 'leverbon' ? undefined : '1%', whiteSpace: 'nowrap', position: 'relative' }} data-testid="acties-header">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <span>Acties</span>
                    <div className="relative" ref={actiesTriggerRef}>
                      {/* iOS-style master toggle */}
                      <div className="inline-flex items-center gap-1.5 cursor-pointer" onClick={(e) => { e.stopPropagation(); openActiesMenu(); }} data-testid="toggle-acties">
                        <IOSToggle checked={anyActiesOn} onChange={toggleAllActies} disabled={!actiesPreferences.ready} size="sm" testid="acties-master-switch" ariaLabel="Acties tonen" />
                        <button type="button" aria-label="Zichtbare documentacties kiezen" aria-expanded={actiesMenuOpen} data-testid="acties-menu-open" onClick={e=>{e.stopPropagation();openActiesMenu();}}
                          className={`inline-flex items-center justify-center w-5 h-5 border border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7] text-gray-500 text-[10px] font-bold transition-colors ${actiesMenuOpen ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white'}`}>▼</button>
                      </div>
                    </div>
                    {actiesPreferences.status === 'error' && <button type="button" className="text-[10px] text-red-700 hover:underline" onClick={e=>{e.stopPropagation();actiesPreferences.retry();}} title={actiesPreferences.error} data-testid="acties-save-retry">Opnieuw bewaren</button>}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedDocs.map(d => {
                const showSignedInvoice = docType === 'leverbon' && mayCreateInvoice && actiesVisible.convert && isSignedUninvoicedDelivery(d);
                return (
                <tr key={d.id} className="cursor-pointer hover:bg-[#F0F4FF] transition-colors align-top" onClick={() => navigate(`/documenten/${d.id}/bewerk`)} data-testid={`doc-row-${d.id}`}>
                  <td onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(d.id)}
                      onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id))}
                      data-testid={`select-${d.id}`}
                    />
                  </td>
                  <td>
                    <span className={docType === 'factuur' ? 'invoice-number' : undefined}>
                      <span className="doc-nr" title={d.id} data-testid={`doc-id-${d.id}`}>{d.id}</span>
                      {docType === 'factuur' && <InvoiceDetails doc={d} clientName={kNaam(d.klant_id, d)} profit={winstKolom ? winstMap?.[d.id] : null} showSent={actiesVisible.send_status} />}
                    </span>
                    {docType !== 'factuur' && d.ogm && <div className="text-[10px] font-mono mt-0.5 text-gray-300" data-testid={`doc-ogm-${d.id}`}>{d.ogm}</div>}
                    {/* 🚚 Voortgang-badge voor bestelbonnen (deelleveringen) */}
                    {docType === 'bestelbon' && voortgangMap[d.id] && (() => {
                      const v = voortgangMap[d.id];
                      if (v.volledig_geleverd) {
                        return (
                          <div
                            className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-green-100 text-green-800 border border-green-200 rounded"
                            title={`Volledig geleverd via ${v.aantal_leverbonnen} leverbon(nen)`}
                            data-testid={`bestelbon-volledig-${d.id}`}
                          >
                            <CheckCircle size={9} weight="fill" /> Volledig geleverd
                          </div>
                        );
                      }
                      if (v.aantal_leverbonnen > 0 && v.totaal_te_leveren > 0) {
                        return (
                          <div
                            className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded"
                            title={`${v.totaal_geleverd}/${v.totaal_besteld} geleverd · ${v.lijnen_open} lijn(en) nog open`}
                            data-testid={`bestelbon-resterend-${d.id}`}
                          >
                            ◐ Resterend {v.totaal_te_leveren} {v.lijnen_open > 0 ? `(${v.lijnen_open} lijn${v.lijnen_open > 1 ? 'en' : ''})` : ''}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </td>
                  <td className="text-xs text-gray-500 whitespace-nowrap">
                    <span data-testid={`doc-date-${d.id}`}>{docType === 'factuur' ? invoiceDate(d.datum) : d.datum}</span>
                  </td>
                  {docType === 'factuur' && <td className="text-xs text-gray-500 whitespace-nowrap" data-testid={`invoice-due-date-${d.id}`}>{invoiceDate(d.verval)}</td>}

                  <td className="font-medium" style={{ maxWidth: 240 }}>
                    <div className={docType === 'factuur' ? 'invoice-client-name' : 'truncate'} title={kNaam(d.klant_id, d)} data-testid={`doc-client-${d.id}`}>{kNaam(d.klant_id, d)}</div>
                    {docType !== 'factuur' && d.bron === 'shop' && (
                      <span
                        className={`mt-0.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${(d.levering_type || 'afhaal') === 'levering' ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-blue-100 text-blue-700 border border-blue-200'}`}
                        title={(() => {
                          if ((d.levering_type || 'afhaal') === 'levering') {
                            const a = d.levering_adres || {};
                            const adresStr = `${a.straat || ''} ${a.nr || ''}\n${a.postcode || ''} ${a.stad || ''}${a.opmerking ? `\nOpm: ${a.opmerking}` : ''}`;
                            return `Levering naar:\n${adresStr}${d.gewenste_datum ? `\n\nGewenst: ${d.gewenste_datum}` : ''}`;
                          }
                          return `Afhaal${d.gewenste_datum ? ` op ${d.gewenste_datum}` : ''}`;
                        })()}
                        data-testid={`lev-badge-${d.id}`}
                      >
                        {(d.levering_type || 'afhaal') === 'levering' ? '🚚 Lev' : '🏪 Afhaal'}
                      </span>
                    )}
                  </td>

                  {/* Verkoper-kolom (alleen bestelbon) */}
                  {docType === 'bestelbon' && (
                    <td className="text-xs">
                      {d.verkoper_naam ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-[#002FA7] border border-blue-200 font-bold" data-testid={`bestelbon-verkoper-${d.id}`}>
                          {d.verkoper_naam}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </td>
                  )}

                  {docType === 'factuur' && winstMap !== null && <td className="right" data-testid={`winst-${d.id}`}>
                    {!winstKolom ? <span title="Winst verborgen">•••</span> : winstMap[d.id]?.volledig ?
                      <span style={{ color: winstMap[d.id].winst >= 0 ? '#008A27' : '#B91C1C' }} title={winstMap[d.id].marge_pct == null ? undefined : `Marge ${winstMap[d.id].marge_pct}%`}>{fmt(winstMap[d.id].winst)}</span> : <span title="Geen aankoopprijs bekend voor deze artikelen">—</span>}
                  </td>}
                  {['factuur', 'leverbon'].includes(docType) && <><td className="right" data-testid={`${docType === 'factuur' ? 'invoice' : 'delivery'}-excl-${d.id}`}>{fmt(d.excl || 0)}</td><td className="right" data-testid={`${docType === 'factuur' ? 'invoice' : 'delivery'}-vat-${d.id}`}>{fmt(d.btw_totaal ?? d.btw_bedrag ?? 0)}</td></>}

                  <td className="right font-medium" data-testid={`doc-total-${d.id}`}>{fmt(d.totaal)}</td>

                  {/* 💰 Winst-kolom (alleen met winst-machtiging, schakelbaar) */}
                  {docType !== 'factuur' && winstMap !== null && (
                    <td className="right" data-testid={`winst-${d.id}`}>
                      {winstKolom ? (() => {
                        const w = winstMap[d.id];
                        if (!w || !w.volledig) return <span className="text-gray-300 text-xs" title="Geen aankoopprijs bekend voor deze artikelen">—</span>;
                        const pos = w.winst >= 0;
                        return (
                          <div className="whitespace-nowrap">
                            <span className={`font-bold text-xs ${pos ? 'text-[#008A27]' : 'text-[#E02424]'}`}>{fmt(w.winst)}</span>
                            {w.marge_pct !== null && w.marge_pct !== undefined && (
                              <span className={`${docType === 'factuur' ? 'block' : `ml-1 px-1 py-0.5 ${pos ? 'bg-green-50' : 'bg-red-50'}`} text-[10px] font-bold ${pos ? 'text-green-700' : 'text-red-700'}`} data-testid={`winst-marge-${d.id}`}>{w.marge_pct}%</span>
                            )}
                          </div>
                        );
                      })() : <span className="text-gray-300 text-xs" title="Winst verborgen — zet de schakelaar in de kolomkop aan">•••</span>}
                    </td>
                  )}

                  {/* Te betalen kolom - houdt rekening met creditnota's én overbetaling */}
                  {docType === 'factuur' && <InvoicePaymentCell doc={d} />}

                  {/* Status pill (klik om aan te passen) */}
                  <td onClick={e => e.stopPropagation()} style={{ width: docType === 'factuur' ? 194 : docType === 'leverbon' ? 164 : 188, verticalAlign: 'middle' }} data-testid={`doc-status-cell-${d.id}`}>
                    <div className={docType === 'factuur' ? 'invoice-status-content' : 'flex items-center gap-x-2 gap-y-1 flex-wrap'}>
                      {docType === 'factuur' ? (
                        <FactuurStatusPill
                          status={d.status}
                          verval={d.verval}
                          betaald_bedrag={d.betaald_bedrag}
                          totaal={d.totaal}
                          options={cfg.statuses}
                          onChange={(v) => changeStatus(d.id, v)}
                          testId={`status-${d.id}`}
                        />
                      ) : (
                        <StatusPillSelect
                          status={d.status}
                          options={cfg.statuses}
                          onChange={(v) => changeStatus(d.id, v)}
                          testId={`status-${d.id}`}
                        />
                      )}
                      {/* Info-elementen inline naast de status pill — leverinfo + gekoppelde documenten als tekstlinks */}
                      {!['factuur', 'leverbon'].includes(docType) && (d.chauffeur_naam || d.verzonden_at || d.geleverd_at || (d.gefactureerd && d.factuur_id) || d.leverbon_id || d.bestelbon_id || (d.creditnota_ids && d.creditnota_ids.length > 0) || (cfg.actionStatus && d.status === cfg.actionStatus && !d.gefactureerd)) && (
                        <div className="flex items-center gap-x-2 flex-wrap text-[10px] text-gray-500 leading-tight" data-testid={`doc-info-${d.id}`}>
                          {docType === 'leverbon' && d.chauffeur_naam && <span className="truncate max-w-[90px]" title={`Chauffeur: ${d.chauffeur_naam}`}>{d.chauffeur_naam}</span>}
                          {docType === 'leverbon' && (d.geleverd_at ? (
                            <span className="text-[#008A27] font-semibold whitespace-nowrap" title={`Geleverd op ${new Date(d.geleverd_at).toLocaleString('nl-BE')}`}>✓ {new Date(d.geleverd_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          ) : d.verzonden_at ? (
                            <span className="whitespace-nowrap" title={`Verzonden op ${new Date(d.verzonden_at).toLocaleString('nl-BE')}`}>🚚 {new Date(d.verzonden_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          ) : null)}
                        {d.gefactureerd && d.factuur_id && (
                          <span
                            className="text-[#7C3AED] font-semibold cursor-pointer hover:underline whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); navigate(`/documenten/${d.factuur_id}/bewerk`); }}
                            title={`Klik om factuur ${d.factuur_id} te openen`}
                            data-testid={`gefactureerd-${d.id}`}
                          >
                            {d.factuur_id}
                          </span>
                        )}
                        {d.leverbon_id && (
                          <span
                            className="text-[#008A27] font-semibold cursor-pointer hover:underline whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); navigate(`/documenten/${d.leverbon_id}/bewerk`); }}
                            title={`Leverbon aangemaakt: ${d.leverbon_id}`}
                            data-testid={`leverbon-aangemaakt-${d.id}`}
                          >
                            {d.leverbon_id}
                          </span>
                        )}
                        {d.bestelbon_id && (
                          <span
                            className="text-amber-600 font-semibold cursor-pointer hover:underline whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); navigate(`/documenten/${d.bestelbon_id}/bewerk`); }}
                            title={`Bestelbon aangemaakt: ${d.bestelbon_id}`}
                            data-testid={`bestelbon-aangemaakt-${d.id}`}
                          >
                            {d.bestelbon_id}
                          </span>
                        )}
                        {d.creditnota_ids && d.creditnota_ids.length > 0 && (
                          <span
                            className="text-rose-600 font-semibold cursor-pointer hover:underline whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); navigate(`/documenten/${d.creditnota_ids[d.creditnota_ids.length - 1]}/bewerk`); }}
                            title={`${d.creditnota_ids.length} creditnota('s): ${d.creditnota_ids.join(', ')}`}
                            data-testid={`creditnotas-${d.id}`}
                          >
                            {d.creditnota_ids.length}× CN
                          </span>
                        )}
                        {cfg.actionStatus && d.status === cfg.actionStatus && !d.gefactureerd && (
                          <span className="text-orange-600 font-bold uppercase text-[9px] whitespace-nowrap">{cfg.actionLabel}</span>
                        )}
                        {docType === 'leverbon' && d.status === 'verzonden' && (
                          <button
                            className="text-[#008A27] font-bold hover:underline whitespace-nowrap"
                            onClick={() => changeStatus(d.id, 'geleverd')}
                            data-testid={`mark-geleverd-${d.id}`}
                          >
                            ✓ Geleverd?
                          </button>
                        )}
                      </div>
                    )}
                    </div>
                  </td>

                  {/* Actions — altijd op één rij (kolom krimpt tot inhoud, nooit wrappen) */}
                  <td onClick={e => e.stopPropagation()} className={docType === 'leverbon' ? 'delivery-actions-cell' : undefined} style={{ whiteSpace: 'nowrap' }} data-testid={`acties-cell-${d.id}`}>
                    <div className={docType === 'leverbon' ? 'delivery-actions-layout' : undefined}>
                    {docType === 'leverbon' && <>
                      <DeliveryStatusDetails doc={d} showSignature={actiesVisible.handtekening} showEmail={actiesVisible.send_status} actionStatus={cfg.actionStatus} actionLabel={cfg.actionLabel} onDelivered={() => changeStatus(d.id, 'geleverd')} />
                      <div className="delivery-invoice-slot" data-testid={`delivery-invoice-slot-${d.id}`}>
                        {showSignedInvoice && <SignedDeliveryInvoice doc={d} busy={!!converting[`${d.id}:factuur`]} onCreate={event => omzetten(d.id, 'factuur', event)} />}
                      </div>
                    </>}
                    <InvoiceRowActions docType={docType} documentId={d.id} primaryActions={actiesPreferences.primary_actions}>
                      {!anyActiesOn && <span className="text-[10px] text-gray-300">—</span>}
                      {actiesVisible.pdf && (
                      <button className="btn btn-ghost btn-sm" onClick={() => generatePdf(d)} title="PDF bekijken" data-testid={`pdf-${d.id}`}>
                        <FilePdf size={14} weight="bold" className="text-[#E02424]" />
                      </button>
                      )}
                      {actiesVisible.download && (
                      <button className="btn btn-ghost btn-sm" onClick={() => downloadPdf(d)} title="PDF downloaden" data-testid={`download-${d.id}`}>
                        <DownloadSimple size={14} weight="bold" className="text-[#059669]" />
                      </button>
                      )}
                      {actiesVisible.pdf && (
                        <RemotePrintButton
                          documentId={d.id}
                          documentType={docType}
                          bestandsnaam={pdfFilename(d)}
                          testid={`remote-print-${d.id}`}
                        />
                      )}
                      {actiesVisible.billit && (d.type === 'factuur' || d.type === 'creditnota') && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={async () => {
                            try {
                              if (d.billit_pushed && d.billit_order_id) {
                                if (!window.confirm(`Deze factuur is al naar Billit verstuurd (ID: ${d.billit_order_id}).\n\nOpnieuw versturen? Dit kan een duplicaat maken in Billit.`)) return;
                              } else {
                                if (!window.confirm(`Verstuur deze factuur naar Billit?\n\nKlant: ${d.klant_naam || kNaam(d.klant_id)}\nBedrag: € ${(d.totaal||0).toFixed(2)}`)) return;
                              }
                              const r = await API.post(`/billit/push-invoice/${d.id}`);
                              if (r.data?.already_pushed) {
                                alert(`✓ Al verstuurd naar Billit (ID: ${r.data.billit_order_id})`);
                              } else {
                                alert(`✓ Verstuurd naar Billit!\n\nBillit Order ID: ${r.data.billit_order_id}`);
                                load();
                              }
                            } catch (e) {
                              const msg = e.response?.data?.detail || e.message;
                              if (msg.toLowerCase().includes('niet actief')) {
                                if (window.confirm(`Billit is nog niet geconfigureerd.\n\nGa nu naar Instellingen → Integraties → Billit om het in te schakelen?`)) {
                                  window.location.href = '/instellingen?tab=integraties';
                                }
                              } else {
                                alert('Naar Billit versturen mislukt:\n\n' + msg);
                              }
                            }
                          }}
                          title={d.billit_pushed ? `✓ Reeds verstuurd naar Billit (#${d.billit_order_id || '?'})` : 'Verstuur naar Billit (boekhouding + Peppol)'}
                          data-testid={`billit-push-${d.id}`}
                          style={d.billit_pushed ? { color: '#10B981' } : { color: '#F59E0B' }}
                        >
                          <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{d.billit_pushed ? '🇧🇪✓' : '🇧🇪'}</span>
                        </button>
                      )}
                      {actiesVisible.download && (typeof navigator !== 'undefined' && navigator.share) && (
                        <button className="btn btn-ghost btn-sm" onClick={() => sharePdfDoc(d)} title="Delen via Mail / AirDrop / andere apps (native share-sheet)" data-testid={`share-pdf-${d.id}`}>
                          <span className="text-[15px]">📤</span>
                        </button>
                      )}
                      {actiesVisible.download && (
                        <button
                          className="btn btn-ghost btn-sm inline-flex items-center gap-0.5"
                          onClick={() => whatsappPdfDoc(d)}
                          title="Deel PDF direct via WhatsApp — link is 24u geldig voor de ontvanger"
                          data-testid={`whatsapp-pdf-${d.id}`}
                        >
                          <WhatsappLogo size={14} className="text-[#25D366]" weight="fill" />
                          <span className="text-[9px] font-bold text-[#25D366] uppercase tracking-tight">PDF</span>
                        </button>
                      )}
                      {actiesVisible.email && (
                      <button className="btn btn-ghost btn-sm" onClick={() => emailDoc(d)} title="E-mail" data-testid={`email-${d.id}`}>
                        <EnvelopeSimple size={14} className="text-[#2563EB]" />
                      </button>
                      )}
                      {actiesVisible.dup && (
                      <button className="btn btn-ghost btn-sm" onClick={() => dupliceer(d.id)} title="Dupliceren" data-testid={`dup-${d.id}`}>
                        <Copy size={14} className="text-gray-500" />
                      </button>
                      )}
                      {actiesVisible.edit && (
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/documenten/${d.id}/bewerk`)} title="Bewerken" data-testid={`edit-${d.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      )}
                      {actiesVisible.chauffeur && docType === 'leverbon' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setChauffeurAdresModal({ leverbonId: d.id })}
                          title="Stuur leveringsadres via WhatsApp naar chauffeur"
                          data-testid={`chauffeur-adres-${d.id}`}
                        >
                          <WhatsappLogo size={14} className="text-emerald-600" weight="fill" />
                        </button>
                      )}
                      {actiesVisible.cmr && docType === 'leverbon' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            const k = klanten.find(x => x.id === d.klant_id);
                            setCmrModal({
                              did: d.id,
                              klantNaam: k?.bedrijf || k?.naam || '',
                            });
                          }}
                          title="CMR (Internationale Vrachtbrief) genereren — 4 gekleurde originelen"
                          data-testid={`cmr-${d.id}`}
                        >
                          <Truck size={14} className="text-[#2563EB]" />
                        </button>
                      )}
                      {actiesVisible.etiket && docType === 'leverbon' && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEtiketModal({ docIds: [d.id] })}
                          title="Adresetiketten — A4-pdf met 2 etiketten van 210 × 148 mm"
                          data-testid={`etiket-${d.id}`}
                        >
                          <Tag size={14} weight="fill" className="text-[#1a1a1a]" />
                        </button>
                      )}
                      {/* 🚚 Gedeeltelijke levering voor bestelbonnen */}
                      {actiesVisible.deellevering && docType === 'bestelbon' && !(voortgangMap[d.id]?.volledig_geleverd) && (
                        <button
                          className="btn btn-ghost btn-sm relative"
                          onClick={() => setDeelLeverModal({ bestelbonId: d.id })}
                          title="Gedeeltelijke levering — maak leverbon voor deel van bestelling"
                          data-testid={`deellevering-btn-${d.id}`}
                        >
                          <Truck size={14} className="text-purple-600" />
                          {voortgangMap[d.id]?.aantal_leverbonnen > 0 && (
                            <span
                              className="absolute -top-1 -right-1 bg-purple-600 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                              data-testid={`deellevering-count-${d.id}`}
                            >
                              {voortgangMap[d.id].aantal_leverbonnen}
                            </span>
                          )}
                        </button>
                      )}
                      {/* 📨 Verzendmelding-knop voor leverbonnen die al verzonden/geleverd zijn */}
                      {actiesVisible.verzendmelding && docType === 'leverbon' && ['verzonden', 'geleverd'].includes((d.status || '').toLowerCase()) && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            const k = klanten.find(x => x.id === d.klant_id);
                            if (!k) { alert('Klant niet gevonden'); return; }
                            const bedrijf = (k.bedrijf || '').trim();
                            const persoon = `${k.voorn || ''} ${k.achtern || ''}`.trim();
                            setMeldVerzendModal({
                              leverbonnen: [{
                                nummer: d.id,
                                klant_id: k.id,
                                klant_naam: bedrijf || persoon || k.naam || '(klant)',
                                email: k.email || '',
                                gsm: k.gsm || k.mobiel || k.tel || k.telefoon || '',
                                taal: (k.taal || 'nl').toString().toLowerCase().slice(0, 2),
                              }],
                              bedrijfsnaam: inst.naam || '',
                            });
                          }}
                          title="Verzendmelding sturen via Mail of WhatsApp"
                          data-testid={`meld-verzending-${d.id}`}
                        >
                          <EnvelopeSimple size={14} className="text-emerald-600" />
                        </button>
                      )}
                      {actiesVisible.handtekening && docType === 'leverbon' && !hasDeliverySignature(d) && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              const k = klanten.find(x => x.id === d.klant_id);
                              setSignModal({
                                did: d.id,
                                klantNaam: k?.naam || k?.bedrijf || '',
                                klantGsm: k?.gsm || k?.tel || '',
                                klantEmail: k?.email || '',
                              });
                            }}
                            title="Laten ondertekenen (QR / tablet)"
                            data-testid={`sign-${d.id}`}
                          >
                            <PenNib size={14} className="text-[#D97706]" />
                          </button>
                      )}
                      {actiesVisible.cn && docType === 'factuur' && ['betaald','openstaand','overbetaald','gedeeltelijk betaald','vervallen'].includes(d.status) && (
                        <button className="btn btn-ghost btn-sm" onClick={() => maakCreditnota(d.id)} title="Creditnota aanmaken (lijn-voor-lijn)" data-testid={`cn-${d.id}`}>
                          <CreditCard size={14} className="text-[#D97706]" />
                        </button>
                      )}
                      {actiesVisible.pay && docType === 'factuur' && d.status !== 'betaald' && d.status !== 'overbetaald' && d.totaal > 0 && (
                        <button className="btn btn-ghost btn-sm" onClick={() => maakBetaallink(d)} title="Betaallink" data-testid={`pay-${d.id}`}>
                          <CurrencyEur size={14} className="text-[#059669]" />
                        </button>
                      )}
                      {actiesVisible.del && (
                      <button className="btn btn-ghost btn-sm text-red-400" onClick={() => del(d.id)} title="Verwijderen" data-testid={`del-${d.id}`}>
                        <Trash size={13} />
                      </button>
                      )}
                      {/* Verzonden/Niet-verstuurd badge - helemaal rechts (toggle via ACTIES-menu) */}
                      {!['factuur', 'leverbon'].includes(docType) && (actiesVisible.send_status && d.laatst_verzonden_op ? (
                        <div
                          className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-green-50 text-green-700 text-[9px] font-bold border border-green-200 whitespace-nowrap"
                          title={`Verstuurd op ${new Date(d.laatst_verzonden_op).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })}${d.email_verzonden_count > 1 ? ` (${d.email_verzonden_count}× totaal)` : ''}`}
                          data-testid={`sent-badge-${d.id}`}
                        >
                          <EnvelopeSimple size={9} weight="fill" /> Verzonden{d.email_verzonden_count > 1 ? ` ${d.email_verzonden_count}×` : ''}
                        </div>
                      ) : actiesVisible.send_status && (docType === 'factuur' || docType === 'offerte' || docType === 'leverbon' || docType === 'bestelbon') && (
                        <div
                          className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-gray-50 text-gray-400 text-[9px] border border-gray-200 whitespace-nowrap"
                          title="Nog niet per e-mail verstuurd naar klant"
                          data-testid={`not-sent-badge-${d.id}`}
                        >
                          <EnvelopeSimple size={9} /> Niet verstuurd
                        </div>
                      ))}
                      {/* Webshop-bestelling: Bevestig/Weiger knoppen (alleen voor bron=shop & status=concept) */}
                      {anyActiesOn && d.bron === 'shop' && (d.status || '').toLowerCase() === 'concept' && (
                        <>
                          <button
                            className="btn btn-sm text-[11px] bg-green-600 hover:bg-green-700 text-white font-bold inline-flex items-center gap-1 px-2"
                            onClick={() => bevestigShop(d.id)}
                            title="Bestelling bevestigen + klant mailen"
                            data-testid={`shop-bevestig-${d.id}`}
                          >
                            <CheckCircle size={12} weight="bold" /> Bevestig
                          </button>
                          <button
                            className="btn btn-sm text-[11px] bg-white border border-red-300 hover:bg-red-50 text-red-600 font-bold inline-flex items-center gap-1 px-2"
                            onClick={() => weigerShop(d.id)}
                            title="Bestelling weigeren + klant mailen"
                            data-testid={`shop-weiger-${d.id}`}
                          >
                            <XCircle size={12} weight="bold" /> Weiger
                          </button>
                        </>
                      )}
                      {!showSignedInvoice && actiesVisible.convert && cfg.actionStatus && d.status === cfg.actionStatus && !d.gefactureerd && !d.factuur_id && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => omzetten(d.id, cfg.actionTarget, e)} data-testid={`factuur-maken-${d.id}`}>
                          <Receipt size={14} weight="bold" /> Factuur maken
                        </button>
                      )}
                      {actiesVisible.convert && cfg.conversions.filter(naar => {
                        if (docType === 'leverbon' && naar === 'factuur' && (showSignedInvoice || d.gefactureerd || d.factuur_id)) return false;
                        // Afgewerkte / volledig geleverde bestelbon: geen leverbon of factuur meer aanmaken
                        if (docType === 'bestelbon' && ((d.status || '').toLowerCase() === 'afgewerkt' || voortgangMap[d.id]?.volledig_geleverd)) return false;
                        // Verberg 'leverbon' als er al een leverbon gekoppeld is (voorkomt dubbele aanmaak)
                        if (naar === 'leverbon' && d.leverbon_id) return false;
                        // Bestelbon met leverbon: factuur mag enkel nog via de leverbon zelf, niet rechtstreeks vanaf de bestelbon
                        if (docType === 'bestelbon' && naar === 'factuur' && d.leverbon_id) return false;
                        // Verberg 'bestelbon' als de offerte al omgezet is
                        if (naar === 'bestelbon' && d.bestelbon_id) return false;
                        if (cfg.actionStatus && d.status === cfg.actionStatus && naar === cfg.actionTarget) return false;
                        return true;
                      }).map(naar => (
                        <button key={naar} className="btn btn-ghost btn-sm text-[11px]" onClick={(e) => omzetten(d.id, naar, e)} title={`Omzetten naar ${TYPE_LABELS[naar]}`} data-testid={`convert-${d.id}-${naar}`}>
                          <ArrowRight size={12} /> {TYPE_LABELS[naar]}
                        </button>
                      ))}
                    </InvoiceRowActions>
                    </div>
                  </td>
                </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={(docType === 'factuur' ? 11 : docType === 'leverbon' ? 9 : docType === 'bestelbon' ? 8 : 7) + (winstMap !== null ? 1 : 0)} className="text-center text-gray-400 py-8">
                  Geen {cfg.label.toLowerCase()} gevonden.{' '}
                  <button className="text-[#002FA7] underline" onClick={() => navigate(`/documenten/nieuw?type=${docType}`)}>
                    Maak een nieuwe {cfg.single.toLowerCase()} aan
                  </button>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginatie-balk — links: aantal per pagina, rechts: pagina-navigatie */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex-wrap" data-testid="doclist-pagination">
            <div className={`flex items-center gap-2 text-[11px] text-gray-600${docType !== 'factuur' ? ' flex-wrap max-w-full' : ''}`}>
              <label className="font-bold uppercase tracking-wider text-gray-500">Per pagina</label>
              <input
                type="number"
                min="1"
                max="1000"
                step="1"
                className="w-20 px-2 py-1 border border-gray-200 rounded text-xs font-mono font-bold text-center focus:outline-none focus:border-[#002FA7]"
                value={pageSize}
                onChange={e => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v > 0 && v <= 1000) setPageSize(v);
                }}
                data-testid="pagesize-input"
                title="Aantal documenten per pagina (1–1000)"
              />
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">
                {(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, filtered.length)} van <strong className="text-gray-700">{filtered.length}</strong>
              </span>
              {[25, 50, 100, 250].map(sz => (
                <button
                  key={sz}
                  type="button"
                  onClick={() => setPageSize(sz)}
                  className={`px-2 py-0.5 text-[10px] font-bold rounded transition-colors ${pageSize === sz ? 'bg-[#002FA7] text-white' : 'text-gray-500 hover:bg-gray-200'}`}
                  data-testid={`pagesize-preset-${sz}`}
                >
                  {sz}
                </button>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center gap-1" data-testid="pagination-nav">
                <button
                  type="button"
                  className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={curPage === 1}
                  onClick={() => setPage(1)}
                  data-testid="pagination-first"
                  title="Eerste pagina"
                >‹‹</button>
                <button
                  type="button"
                  className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={curPage === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  data-testid="pagination-prev"
                  title="Vorige pagina"
                >‹</button>
                {/* Toon max 7 pagina-nummers rondom huidige pagina */}
                {(() => {
                  const pages = [];
                  let start = Math.max(1, curPage - 3);
                  let end = Math.min(totalPages, start + 6);
                  if (end - start < 6) start = Math.max(1, end - 6);
                  for (let p = start; p <= end; p++) pages.push(p);
                  return pages.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      className={`min-w-[28px] px-2 py-1 text-xs font-bold rounded transition-colors ${p === curPage ? 'bg-[#D97706] text-white' : 'text-gray-600 hover:bg-gray-200'}`}
                      data-testid={`pagination-page-${p}`}
                    >{p}</button>
                  ));
                })()}
                <button
                  type="button"
                  className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={curPage === totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  data-testid="pagination-next"
                  title="Volgende pagina"
                >›</button>
                <button
                  type="button"
                  className="px-2 py-1 text-xs font-bold text-gray-500 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={curPage === totalPages}
                  onClick={() => setPage(totalPages)}
                  data-testid="pagination-last"
                  title="Laatste pagina"
                >››</button>
              </div>
            )}
          </div>
        )}
      </div>

      {docType === 'factuur' && <div className="flex justify-end" data-testid="invoice-back-to-top-slot" />}

      {/* Acties dropdown - fixed position overlay (renders outside overflow container) */}
      {actiesMenuOpen && (
        <div
          ref={actiesMenuRef}
          className="fixed bg-white border border-gray-200 shadow-2xl z-50 w-[320px] max-w-[calc(100vw-16px)] overflow-y-auto"
          style={{ top: actiesMenuPos.top, left: actiesMenuPos.left, width: actiesMenuPos.width, maxHeight: `calc(100dvh - ${actiesMenuPos.top + 8}px)` }}
          onClick={e => e.stopPropagation()}
          data-testid="acties-menu"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50">
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Persoonlijke acties</span>
            <button type="button" className="text-[11px] text-[#002FA7] hover:underline font-bold disabled:opacity-40" disabled={!actiesPreferences.ready} onClick={toggleAllActies} data-testid="acties-menu-master-toggle">
              {anyActiesOn ? 'Alles uit' : 'Alles aan'}
            </button>
          </div>
          <DocumentActionPins type={docType} preferences={actiesPreferences} />
          <div className="px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-500">Beschikbare acties · overige onder ⋯</div>
          {[
            { k: 'pdf', label: 'PDF bekijken' },
            { k: 'download', label: 'PDF downloaden' },
            { k: 'email', label: 'E-mail versturen' },
            { k: 'dup', label: 'Dupliceren' },
            { k: 'edit', label: 'Bewerken' },
            docType === 'factuur' && { k: 'cn', label: 'Creditnota maken' },
            docType === 'factuur' && { k: 'pay', label: 'Betaallink' },
            ['factuur','creditnota'].includes(docType) && { k: 'billit', label: 'Billit versturen (🇧🇪)' },
            ['offerte','bestelbon','leverbon','proforma'].includes(docType) && { k: 'convert', label: 'Omzetten' },
            docType === 'bestelbon' && { k: 'deellevering', label: 'Gedeeltelijke levering (🚚)' },
            docType === 'leverbon' && { k: 'chauffeur', label: 'WhatsApp naar chauffeur' },
            docType === 'leverbon' && { k: 'cmr', label: 'CMR vrachtbrief' },
            docType === 'leverbon' && { k: 'etiket', label: 'Adresetiketten (A4, 210 × 148 mm)' },
            docType === 'leverbon' && { k: 'verzendmelding', label: 'Verzendmelding sturen' },
            docType === 'leverbon' && { k: 'handtekening', label: 'Handtekening (ondertekenen / status)' },
            { k: 'send_status', label: 'Verzendstatus (Verzonden / Niet verstuurd)' },
            { k: 'del', label: 'Verwijderen' },
          ].filter(Boolean).map(a => (
            <div key={a.k} className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-gray-50 text-[13px] text-gray-700" data-testid={`acties-opt-${a.k}`}>
              <span className="flex-1 font-medium">{a.label}</span>
              <IOSToggle checked={!!actiesPreferences.actions[a.k]} onChange={() => toggleActie(a.k)} disabled={!actiesPreferences.ready} size="sm" showLabel={false} testid={`acties-switch-${a.k}`} ariaLabel={a.label} />
            </div>
          ))}
          <div className={`px-4 py-2 text-[11px] border-t border-gray-100 ${actiesPreferences.status==='error'?'bg-red-50 text-red-700':'bg-gray-50 text-gray-500'}`} role="status" aria-live="polite" data-testid="acties-save-status">
            {actiesPreferences.status==='loading'?'Voorkeur laden…':actiesPreferences.status==='saving'?'Voorkeur bewaren…':actiesPreferences.status==='error'?actiesPreferences.error:'Bewaard op je account'}
            {actiesPreferences.status==='error' && <button type="button" className="block mt-1 font-semibold underline" onClick={actiesPreferences.retry} data-testid="acties-menu-save-retry">Opnieuw bewaren</button>}
          </div>
        </div>
      )}

      {/* Versturen modal (Peppol of E-mail voor facturen) */}
      {versturenModal && (() => {
        const { doc, opties, historiek, busy } = versturenModal;
        const k = kFull(doc.klant_id, doc);
        const peppolOk = opties?.peppol?.enabled;
        const emailOk = opties?.email?.enabled;
        return (
          <Dialog open onOpenChange={open => { if (!open && !busy) setVersturenModal(null); }}>
            <DialogContent className="bg-white text-gray-900 w-[calc(100vw-24px)] max-w-lg max-h-[90dvh] overflow-y-auto p-0 gap-0 [&>button:last-child]:hidden" data-testid="versturen-modal" aria-describedby={undefined}
              onCloseAutoFocus={e => { e.preventDefault(); document.querySelector(`[data-testid="email-${CSS.escape(doc.id)}"]`)?.focus(); }}>
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <div>
                  <DialogTitle className="text-base font-bold text-[#0A0A0A]">Factuur versturen</DialogTitle>
                  <p className="text-xs text-gray-500 mt-0.5">{doc.id} — {k.bedrijf || `${k.voorn} ${k.achtern}`.trim()}</p>
                </div>
                <button className="text-gray-400 hover:text-gray-700 text-xl leading-none" onClick={() => !busy && setVersturenModal(null)} aria-label="Verzendvenster sluiten" data-testid="versturen-close">×</button>
              </div>

              {!opties ? (
                <div className="p-8 text-center text-sm text-gray-400">Laden…</div>
              ) : (
                <div className="p-5 space-y-4">
                  {/* Peppol optie */}
                  <div className={`border p-4 ${peppolOk ? 'border-[#059669]/40 bg-green-50/40' : 'border-gray-200 bg-gray-50/40'}`} data-testid="versturen-peppol-card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-wider text-[#059669]">Peppol</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 ${peppolOk ? 'bg-[#059669] text-white' : 'bg-gray-300 text-gray-600'}`}>
                            {peppolOk ? 'AANBEVOLEN' : 'NIET BESCHIKBAAR'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 leading-snug">
                          {peppolOk
                            ? <>Directe levering via het officiële Peppol-netwerk naar <strong className="font-mono">{opties.peppol.display}</strong></>
                            : <>{opties.peppol.reason}. Schakel de Peppol-vlag in bij de klant indien aangesloten.</>}
                        </div>
                      </div>
                      <button
                        className="btn btn-sm inline-flex items-center gap-1.5 shrink-0"
                        style={{ background: peppolOk ? '#059669' : '#D1D5DB', color: peppolOk ? '#fff' : '#6B7280', cursor: peppolOk && !busy ? 'pointer' : 'not-allowed' }}
                        disabled={!peppolOk || busy}
                        onClick={verstuurPeppol}
                        data-testid="versturen-peppol-btn"
                      >
                        Verzenden via Peppol
                      </button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between gap-3">
                      <div className="text-[10px] text-gray-500 leading-snug flex-1">
                        💡 <strong>UBL 2.1 / Peppol BIS 3.0 XML</strong> — Download het wettelijk verplichte e-factuur-formaat voor handmatige upload naar je Peppol Access Point (Codabox, Ibanity, Unifiedpost, …) of voor archivering.
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm inline-flex items-center gap-1.5 shrink-0 bg-white border border-[#059669] text-[#059669] hover:bg-[#059669]/10"
                        onClick={async () => {
                          try {
                            const r = await API.get(`/documenten/${versturenModal.doc.id}/ubl-xml`, { responseType: 'blob' });
                            const blob = new Blob([r.data], { type: 'application/xml' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${versturenModal.doc.id}_peppol_bis_3_0.xml`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            alert('UBL-XML download mislukt: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                        data-testid="versturen-peppol-xml-download"
                        title="Download UBL 2.1 / Peppol BIS 3.0 XML"
                      >
                        📄 XML downloaden
                      </button>
                    </div>
                  </div>

                  {/* E-mail optie */}
                  <div className={`border p-4 ${emailOk ? 'border-[#002FA7]/40 bg-[#F0F4FF]/40' : 'border-gray-200 bg-gray-50/40'}`} data-testid="versturen-email-card">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-wider text-[#002FA7]">E-mail</span>
                          {!peppolOk && emailOk && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-[#002FA7] text-white">AANBEVOLEN</span>}
                          {!emailOk && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-gray-300 text-gray-600">GEEN ADRES</span>}
                        </div>
                        <div className="text-xs text-gray-600 leading-snug">
                          {emailOk
                            ? <>PDF verzenden naar <strong>{opties.email.to}</strong></>
                            : <>{opties.email.reason}. Voeg eerst een e-mailadres toe bij de klant.</>}
                        </div>
                      </div>
                      <button
                        className="btn btn-sm inline-flex items-center gap-1.5 shrink-0"
                        style={{ background: emailOk ? '#002FA7' : '#D1D5DB', color: emailOk ? '#fff' : '#6B7280', cursor: emailOk && !busy ? 'pointer' : 'not-allowed' }}
                        disabled={!emailOk || busy}
                        onClick={verstuurEmail}
                        data-testid="versturen-email-btn"
                      >
                        Voorbeeld & Verzenden
                      </button>
                    </div>
                  </div>

                  {/* Historiek */}
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Verzendhistoriek</div>
                    {historiek.length === 0 ? (
                      <div className="text-xs text-gray-400 italic py-2">Nog geen verzendingen geregistreerd.</div>
                    ) : (
                      <div className="border border-gray-100 divide-y divide-gray-100" data-testid="versturen-historiek">
                        {historiek.map((h, i) => (
                          <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs">
                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${h.type === 'peppol' ? 'bg-[#059669] text-white' : 'bg-[#002FA7] text-white'}`}>{h.type}</span>
                            <span className="text-gray-700 flex-1 truncate">{h.correspondent || h.omschrijving || '-'}</span>
                            <span className="text-gray-400 shrink-0">{h.verzonden_at ? new Date(h.verzonden_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Verzend modal (leverbon: kies chauffeur, registreer datum/uur) */}
      {verzendModal && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setVerzendModal(null)} data-testid="verzend-modal">
          <div className="bg-white border border-gray-200 shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="text-base font-bold text-[#0A0A0A]">Leverbon verzenden</h3>
              <p className="text-xs text-gray-500 mt-1">Datum en uur worden automatisch geregistreerd ({new Date().toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}).</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Chauffeur (optioneel)</label>
                {chauffeurs.length === 0 ? (
                  <div className="text-xs text-gray-400 italic py-2">Nog geen chauffeurs. Voeg ze toe via <span className="font-medium text-[#002FA7]">Instellingen → Chauffeurs</span>.</div>
                ) : (
                  <select
                    className="field-input w-full"
                    value={verzendModal.chauffeur_id}
                    onChange={e => setVerzendModal(s => ({ ...s, chauffeur_id: e.target.value }))}
                    data-testid="verzend-chauffeur-select"
                  >
                    <option value="">— Geen / anders —</option>
                    {chauffeurs.filter(c => c.actief !== false).map(c => (
                      <option key={c.id} value={c.id}>
                        {c.naam}{c.nummerplaat ? ` (${c.nummerplaat})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
              <button className="btn btn-ghost btn-sm" onClick={() => setVerzendModal(null)} data-testid="verzend-cancel">Annuleren</button>
              <button className="btn btn-primary btn-sm inline-flex items-center gap-1.5" onClick={bevestigVerzend} data-testid="verzend-confirm">
                🚚 Verzenden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick sign modal (leverbon: laten ondertekenen via QR/tablet) */}
      {signModal && (
        <QuickSignModal
          docId={signModal.did}
          klantNaam={signModal.klantNaam}
          klantGsm={signModal.klantGsm}
          klantEmail={signModal.klantEmail}
          onClose={() => setSignModal(null)}
          onSigned={() => { setSignModal(null); load(true); }}
        />
      )}

      {/* ─── Verzendmelding modal: keuze Mail / WhatsApp na succesvolle verzending ─── */}
      {meldVerzendModal && (
        <VerzendMeldingModal
          leverbonnen={meldVerzendModal.leverbonnen}
          bedrijfsnaam={meldVerzendModal.bedrijfsnaam}
          onClose={() => setMeldVerzendModal(null)}
        />
      )}

      {/* CMR modal (leverbon: internationale vrachtbrief generator) */}
      {cmrModal && (
        <CMRModal
          docId={cmrModal.did}
          klantNaam={cmrModal.klantNaam}
          onClose={() => setCmrModal(null)}
        />
      )}

      {etiketModal && <AdresEtiketModal docIds={etiketModal.docIds} onClose={() => setEtiketModal(null)} />}

      {/* Partial creditnota modal (factuur: lijn-voor-lijn crediteren) */}
      {partialCnModal && (
        <PartialCreditnotaModal
          docId={partialCnModal.did}
          onClose={() => setPartialCnModal(null)}
          onCreated={() => { setPartialCnModal(null); load(); }}
        />
      )}

      {/* DeelLevering modal (bestelbon: gedeeltelijke levering → nieuwe leverbon) */}
      {deelLeverModal && (
        <DeelLeveringModal
          bestelbonId={deelLeverModal.bestelbonId}
          onClose={() => setDeelLeverModal(null)}
          onCreated={() => {
            // Herlaad documenten + voortgang
            load();
            API.post('/bestelbon/voortgang-bulk', { ids: bestelbonIds })
              .then(r => setVoortgangMap(r.data || {}))
              .catch(() => {});
          }}
        />
      )}

      {/* 📱 Stuur leveringsadres via WhatsApp naar chauffeur (single of bulk) */}
      {chauffeurAdresModal && (
        <ChauffeurAdresModal
          leverbonId={chauffeurAdresModal.leverbonId}
          leverbonIds={chauffeurAdresModal.leverbonIds}
          onClose={() => setChauffeurAdresModal(null)}
        />
      )}

      {/* Betalingsherinnering-modal (bulk vanuit facturenlijst: klant zoeken/filteren, facturen selecteren, herinnering opmaken) */}
      {bhModal && (
        <BetalingsherinneringModal
          open={!!bhModal}
          onClose={() => setBhModal(null)}
          klantId={bhModal.klantId}
          klantNaam={kNaam(bhModal.klantId)}
          klantEmail={kFull(bhModal.klantId).email || ''}
          klantGsm={kFull(bhModal.klantId).gsm || kFull(bhModal.klantId).tel || ''}
          klantTaal={kFull(bhModal.klantId).taal || 'nl'}
          preselectIds={bhModal.ids || []}
        />
      )}


      {/* Excel-export modal: datumbereik + status + lijnen */}
      {showExcelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowExcelModal(false)} data-testid="excel-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900">📊 Excel-export</h2>
              <p className="text-xs text-gray-500 mt-0.5">Exporteer {cfg.label.toLowerCase()} naar Excel met alle gegevens.</p>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Snelle keuze</label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  <button className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded font-medium" onClick={() => setRangeMonth(0)}>Deze maand</button>
                  <button className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded font-medium" onClick={() => setRangeMonth(-1)}>Vorige maand</button>
                  <button className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded font-medium" onClick={setRangeYTD}>Year-to-date</button>
                  <button className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded font-medium" onClick={() => setRangeYear(new Date().getFullYear())}>{new Date().getFullYear()}</button>
                  <button className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded font-medium" onClick={() => setRangeYear(new Date().getFullYear() - 1)}>{new Date().getFullYear() - 1}</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Van</label>
                  <input type="date" className="field-input mt-1" value={excelForm.van} onChange={(e) => setExcelForm(p => ({ ...p, van: e.target.value }))} data-testid="excel-van" />
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Tot</label>
                  <input type="date" className="field-input mt-1" value={excelForm.tot} onChange={(e) => setExcelForm(p => ({ ...p, tot: e.target.value }))} data-testid="excel-tot" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Status</label>
                <select className="field-input mt-1" value={excelForm.status} onChange={(e) => setExcelForm(p => ({ ...p, status: e.target.value }))} data-testid="excel-status">
                  <option value="">Alle statussen</option>
                  {(cfg.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Klant (optioneel — zoek op naam, e-mail of BTW)</label>
                <div className="relative">
                  <input
                    type="text"
                    className="field-input mt-1 pr-8"
                    value={excelForm.klant_zoek}
                    onChange={(e) => { setExcelForm(p => ({ ...p, klant_zoek: e.target.value, klant_id: e.target.value ? p.klant_id : '' })); setShowKlantDropdown(true); }}
                    onFocus={() => setShowKlantDropdown(true)}
                    placeholder="Type om te zoeken... (of laat leeg voor alle klanten)"
                    data-testid="excel-klant-zoek"
                  />
                  {(excelForm.klant_zoek || excelForm.klant_id) && (
                    <button
                      type="button"
                      onClick={() => selectKlant(null)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 text-sm"
                      title="Wis selectie"
                    >✕</button>
                  )}
                  {showKlantDropdown && filteredKlanten.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto" data-testid="excel-klant-dropdown">
                      {filteredKlanten.map(k => {
                        const naam = k.bedrijf || `${k.voorn || ''} ${k.achtern || ''}`.trim() || k.id;
                        const selected = k.id === excelForm.klant_id;
                        return (
                          <button
                            key={k.id}
                            type="button"
                            onClick={() => selectKlant(k)}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex flex-col gap-0.5 ${selected ? 'bg-[#EEF3FF]' : ''}`}
                            data-testid={`excel-klant-${k.id}`}
                          >
                            <span className="font-semibold text-gray-900">{naam}</span>
                            {(k.email || k.btwnr) && (
                              <span className="text-[10px] text-gray-500">{k.email}{k.email && k.btwnr ? ' · ' : ''}{k.btwnr}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {selectedKlant && (
                  <div className="text-[10px] text-[#059669] mt-1">✓ Geselecteerd: {selectedKlant.bedrijf || `${selectedKlant.voorn || ''} ${selectedKlant.achtern || ''}`.trim()}</div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm pt-1 cursor-pointer">
                <input type="checkbox" checked={excelForm.include_lijnen} onChange={(e) => setExcelForm(p => ({ ...p, include_lijnen: e.target.checked }))} className="accent-[#002FA7]" data-testid="excel-include-lijnen" />
                <span className="text-gray-700">Inclusief factuurlijnen (extra Excel-sheet)</span>
              </label>

              {/* Verzendmethode: download of email */}
              <div className="pt-3 border-t border-gray-100">
                <div className="text-[11px] font-bold uppercase tracking-wide text-gray-600 mb-2">Verzendmethode</div>
                <div className="grid grid-cols-2 gap-2">
                  <label className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${!excelForm.via_email ? 'border-[#002FA7] bg-[#EEF3FF]' : 'border-gray-300 bg-white hover:bg-gray-50'}`}>
                    <input type="radio" checked={!excelForm.via_email} onChange={() => setExcelForm(p => ({ ...p, via_email: false }))} className="accent-[#002FA7]" data-testid="excel-mode-download" />
                    <span className="text-sm font-medium">⬇️ Download</span>
                  </label>
                  <label className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${excelForm.via_email ? 'border-[#002FA7] bg-[#EEF3FF]' : 'border-gray-300 bg-white hover:bg-gray-50'}`}>
                    <input type="radio" checked={excelForm.via_email} onChange={() => setExcelForm(p => ({ ...p, via_email: true }))} className="accent-[#002FA7]" data-testid="excel-mode-email" />
                    <span className="text-sm font-medium">📧 Verstuur per e-mail</span>
                  </label>
                </div>
              </div>

              {excelForm.via_email && (
                <div className="space-y-2.5 p-3 bg-gray-50 rounded border border-gray-200" data-testid="excel-email-block">
                  {/* Verzendmethode: via Mail Hub gekoppelde mailbox of via systeem */}
                  {excelMailboxen.length > 0 && (
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">
                        📬 Verzenden via
                      </label>
                      <select
                        className="field-input mt-1"
                        value={excelForm.via_mailbox}
                        onChange={(e) => setExcelForm(p => ({ ...p, via_mailbox: e.target.value }))}
                        data-testid="excel-via-mailbox"
                      >
                        <option value="">
                          ✨ Auto: standaard mailbox ({(excelMailboxen.find(a => a.is_default) || excelMailboxen[0])?.email})
                        </option>
                        {excelMailboxen.map(a => (
                          <option key={a.id} value={a.id}>
                            📥 {a.email}{a.is_default ? ' (standaard)' : ''}
                          </option>
                        ))}
                        <option value="skip">⚙️ Via BillFac-systeem (Resend/SMTP)</option>
                      </select>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        {excelForm.via_mailbox === 'skip'
                          ? 'Verstuurd via BillFac-systeem. Verschijnt NIET in je eigen Verzonden-folder.'
                          : '✓ Mail verschijnt in je eigen Outlook/Gmail "Verzonden"-folder.'}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Verstuur naar (To)</label>
                    <input
                      type="email"
                      className="field-input mt-1"
                      value={excelForm.to_email}
                      onChange={(e) => setExcelForm(p => ({ ...p, to_email: e.target.value }))}
                      placeholder="naam@bedrijf.be"
                      data-testid="excel-to-email"
                    />
                    {excelForm.klant_id && (
                      <div className="text-[10px] text-gray-500 mt-0.5">Klant-email auto-gevuld bij klantkeuze</div>
                    )}
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">CC <span className="font-normal text-gray-400 normal-case">(optioneel, komma-gescheiden)</span></label>
                    <input
                      type="text"
                      className="field-input mt-1"
                      value={excelForm.cc_email}
                      onChange={(e) => setExcelForm(p => ({ ...p, cc_email: e.target.value }))}
                      placeholder="boekhouder@firma.be, kopie@firma.be"
                      data-testid="excel-cc-email"
                    />
                  </div>

                  {/* Toon + betaaltermijn + Taal + AI opmaak */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Toon</label>
                      <select
                        className="field-input mt-1"
                        value={excelForm.email_toon}
                        onChange={(e) => setExcelForm(p => ({ ...p, email_toon: e.target.value, betaaltermijn_dagen: '' }))}
                        data-testid="excel-toon"
                      >
                        <option value="vriendelijk">😊 Vriendelijk (info)</option>
                        <option value="herinnering">⚠️ Herinnering (waarschuwing)</option>
                        <option value="aanmaning">🚨 Aanmaning (ingebrekestelling)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Betaaltermijn (dagen)</label>
                      <input
                        type="number"
                        min="1"
                        max="90"
                        className="field-input mt-1"
                        value={excelForm.betaaltermijn_dagen}
                        onChange={(e) => setExcelForm(p => ({ ...p, betaaltermijn_dagen: e.target.value }))}
                        placeholder={excelForm.email_toon === 'aanmaning' ? '5' : excelForm.email_toon === 'herinnering' ? '7' : '30'}
                        data-testid="excel-betaaltermijn"
                      />
                    </div>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Taal</label>
                      <select
                        className="field-input mt-1"
                        value={excelForm.email_taal}
                        onChange={(e) => setExcelForm(p => ({ ...p, email_taal: e.target.value }))}
                        data-testid="excel-taal"
                      >
                        <option value="Nederlands">🇳🇱 Nederlands</option>
                        <option value="Frans">🇫🇷 Frans</option>
                        <option value="Engels">🇬🇧 Engels</option>
                        <option value="Duits">🇩🇪 Duits</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={doAiOpmaak}
                      disabled={aiBusy}
                      className="px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-xs font-bold rounded shadow-sm transition-all disabled:opacity-50 inline-flex items-center gap-1.5"
                      title="Laat AI een professioneel onderwerp + bericht opstellen"
                      data-testid="excel-ai-opmaak"
                    >
                      {aiBusy ? '⏳ Bezig...' : '✨ AI opmaak'}
                    </button>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Onderwerp <span className="font-normal text-gray-400 normal-case">(laat leeg voor automatisch)</span></label>
                    <input
                      type="text"
                      className="field-input mt-1"
                      value={excelForm.email_onderwerp}
                      onChange={(e) => setExcelForm(p => ({ ...p, email_onderwerp: e.target.value }))}
                      placeholder="Documenten-export voor je administratie"
                      data-testid="excel-onderwerp"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Bericht</label>
                    <textarea
                      className="field-input mt-1 text-sm font-mono"
                      rows={8}
                      value={excelForm.email_bericht}
                      onChange={(e) => setExcelForm(p => ({ ...p, email_bericht: e.target.value }))}
                      placeholder="Beste, in bijlage de gevraagde export... (of klik ✨ AI opmaak)"
                      data-testid="excel-bericht"
                    />
                    {excelForm.email_bericht && (
                      <div className="text-[10px] text-gray-400 mt-1 text-right">{excelForm.email_bericht.length} tekens</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2 rounded-b-lg flex-shrink-0">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowExcelModal(false)} disabled={excelBusy}>Annuleer</button>
              {excelForm.via_email && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  onClick={doOpenInMailApp}
                  disabled={excelBusy}
                  title="Download Excel + open je standaard mail-app (Outlook, Apple Mail, Gmail-web...) met onderwerp en bericht vooraf ingevuld"
                  data-testid="excel-open-mailapp"
                >
                  📨 Open in mail-app
                </button>
              )}
              <button className="btn btn-primary btn-sm inline-flex items-center gap-1.5" onClick={doExcelExport} disabled={excelBusy} data-testid="excel-confirm">
                {excelBusy
                  ? '⏳ Bezig...'
                  : excelForm.via_email
                    ? <><EnvelopeSimple size={14} weight="bold" /> Verstuur per e-mail</>
                    : <><DownloadSimple size={14} weight="bold" /> Download Excel</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mail preview-popup voor offerte/proforma/bestelbon/leverbon */}
      {previewDoc && (
        <MailPreviewModal
          docId={previewDoc.id}
          lang="auto"
          onClose={() => setPreviewDoc(null)}
          onSent={() => { setPreviewDoc(null); load(); }}
        />
      )}
    </div>
  );
}
